// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AdminDashboardStats
 *
 * Dashboard statistics response for platform admin.
 */
export const zAdminDashboardStats = z.object({
    total_organizations: z.int(),
    total_users: z.int(),
    active_users: z.int(),
    inactive_users: z.int()
});

/**
 * AgentActionResponse
 *
 * Response DTO for an agent action.
 */
export const zAgentActionResponse = z.object({
    type: z.string(),
    payload: z.record(z.string(), z.unknown())
});

/**
 * AgentFeedbackRequest
 *
 * Request DTO for submitting feedback on an agent response.
 */
export const zAgentFeedbackRequest = z.object({
    query_id: z.uuid(),
    rating: z.enum(['positive', 'negative']),
    reason: z.string().max(50).nullish(),
    comment: z.string().max(500).nullish(),
    question: z.string().max(1000),
    answer: z.string().max(10000),
    session_id: z.string().max(64).nullish()
});

/**
 * AgentQueryRequest
 *
 * Request DTO for agent query.
 */
export const zAgentQueryRequest = z.object({
    question: z.string().min(3).max(1000),
    session_id: z.string().max(64).nullish()
});

/**
 * AgentResponseStatus
 *
 * Status of the agent response.
 */
export const zAgentResponseStatus = z.enum([
    'completed',
    'rejected',
    'clarification_needed',
    'error'
]);

/**
 * AttachmentBlockPreviewResponse
 */
export const zAttachmentBlockPreviewResponse = z.object({
    kind: z.literal('attachment')
});

/**
 * AuthChangePasswordRequest
 *
 * Request model for password change
 */
export const zAuthChangePasswordRequest = z.object({
    old_password: z.string(),
    new_password: z.string().min(8)
});

/**
 * AuthForgotPasswordRequest
 *
 * Request model for password reset
 */
export const zAuthForgotPasswordRequest = z.object({
    token: z.string(),
    new_password: z.string().min(8)
});

/**
 * AuthLoginRequest
 *
 * Request model for user login
 */
export const zAuthLoginRequest = z.object({
    email: z.email(),
    password: z.string()
});

/**
 * AuthLogoutRequest
 *
 * Request model for user logout
 */
export const zAuthLogoutRequest = z.object({
    refresh_token: z.string()
});

/**
 * AuthRefreshRequest
 *
 * Request model for token refresh
 */
export const zAuthRefreshRequest = z.object({
    refresh_token: z.string()
});

/**
 * AuthResetPasswordRequest
 *
 * Request model for password reset
 */
export const zAuthResetPasswordRequest = z.object({
    email: z.email()
});

/**
 * AuthSwitchOrganizationRequest
 *
 * Request model for organization switching
 */
export const zAuthSwitchOrganizationRequest = z.object({
    organization_uuid: z.uuid()
});

/**
 * AuthTokenResponse
 *
 * Response model for authentication tokens
 */
export const zAuthTokenResponse = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.int(),
    token_type: z.string()
});

/**
 * AuthVerifyEmailRequest
 *
 * Request model for email verification
 */
export const zAuthVerifyEmailRequest = z.object({
    token: z.string()
});

/**
 * BeneficiaryBankAccountCreateRequest
 *
 * Request model for creating a bank account
 */
export const zBeneficiaryBankAccountCreateRequest = z.object({
    bank_name: z.string().nullish(),
    iban: z.string(),
    swift: z.string().nullish(),
    is_primary: z.boolean().optional().default(true)
});

/**
 * BeneficiaryBankAccountResponse
 *
 * Response model for bank account data
 */
export const zBeneficiaryBankAccountResponse = z.object({
    uuid: z.uuid(),
    bank_name: z.string().nullable(),
    iban: z.string(),
    swift: z.string().nullable(),
    has_certificate: z.boolean(),
    is_primary: z.boolean(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * BeneficiaryBankAccountUpdateRequest
 *
 * Request model for updating a bank account
 */
export const zBeneficiaryBankAccountUpdateRequest = z.object({
    bank_name: z.string().nullish(),
    iban: z.string().nullish(),
    swift: z.string().nullish(),
    is_primary: z.boolean().nullish()
});

/**
 * BeneficiaryLegalRepresentativeCreateRequest
 *
 * Request model for creating a legal representative
 */
export const zBeneficiaryLegalRepresentativeCreateRequest = z.object({
    first_name: z.string(),
    last_name: z.string(),
    dni: z.string(),
    position: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    is_primary: z.boolean().optional().default(true),
    is_signer: z.boolean().optional().default(true)
});

/**
 * BeneficiaryLegalRepresentativeResponse
 *
 * Response model for legal representative data
 */
export const zBeneficiaryLegalRepresentativeResponse = z.object({
    uuid: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    full_name: z.string(),
    dni: z.string(),
    position: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    is_primary: z.boolean(),
    is_signer: z.boolean(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * BeneficiaryLegalRepresentativeUpdateRequest
 *
 * Request model for updating a legal representative
 */
export const zBeneficiaryLegalRepresentativeUpdateRequest = z.object({
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    dni: z.string().nullish(),
    position: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    is_primary: z.boolean().nullish(),
    is_signer: z.boolean().nullish()
});

/**
 * BeneficiaryStatusBreakdownResponse
 *
 * Response model for beneficiary status breakdown.
 */
export const zBeneficiaryStatusBreakdownResponse = z.object({
    active: z.int(),
    inactive: z.int()
});

/**
 * BeneficiaryMetricsResponse
 *
 * Response model for aggregated beneficiary metrics.
 * Used for the /beneficiaries/metrics endpoint to provide summary KPIs.
 */
export const zBeneficiaryMetricsResponse = z.object({
    total_count: z.int(),
    by_status: zBeneficiaryStatusBreakdownResponse,
    total_capacity: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * BlockVariantResponse
 *
 * Response schema for a single block variant.
 */
export const zBlockVariantResponse = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string()
});

/**
 * Body_create_project_poster
 */
export const zBodyCreateProjectPoster = z.object({
    poster_file: z.string()
});

/**
 * Body_upload_investment_document
 */
export const zBodyUploadInvestmentDocument = z.object({
    document: z.string()
});

/**
 * Body_upload_payment_receipt
 */
export const zBodyUploadPaymentReceipt = z.object({
    file: z.string()
});

/**
 * BulkDeleteClassificationsRequest
 *
 * Payload for the bulk-delete endpoint (fase4-fixes.md B4).
 */
export const zBulkDeleteClassificationsRequest = z.object({
    classification_uuids: z.array(z.uuid()).min(1).max(200)
});

/**
 * BulkDeleteClassificationsResponse
 *
 * Response for the bulk-delete endpoint. `missing_uuids` covers
 * classifications that no longer existed (typically a race with another
 * user).
 */
export const zBulkDeleteClassificationsResponse = z.object({
    deleted_uuids: z.array(z.uuid()),
    missing_uuids: z.array(z.uuid())
});

/**
 * BulkReclassifyClassificationsRequest
 *
 * Payload for the bulk-reclassify endpoint.
 */
export const zBulkReclassifyClassificationsRequest = z.object({
    classification_uuids: z.array(z.uuid()).min(1).max(200)
});

/**
 * BulkReclassifyClassificationsResponse
 *
 * Response for the bulk-reclassify endpoint.
 *
 * Buckets so the toast can explain partial outcomes:
 * - `reclassified_uuids`: docs reset to QUEUED with a reprocess event enqueued.
 * - `skipped_uuids`: docs whose current status disallows reclassify
 * (PROCESSING, DUPLICATE_DETECTED, …), so the request is a no-op.
 * - `missing_uuids`: docs that no longer exist (race with delete or
 * stale page on the reviewer side).
 */
export const zBulkReclassifyClassificationsResponse = z.object({
    reclassified_uuids: z.array(z.uuid()),
    skipped_uuids: z.array(z.uuid()),
    missing_uuids: z.array(z.uuid())
});

/**
 * CategorizationData
 *
 * Categorization result from the expense categorizer stage.
 */
export const zCategorizationData = z.object({
    category_id: z.string().nullish(),
    category_name: z.string().nullish(),
    subcategory_id: z.string().nullish(),
    subcategory_name: z.string().nullish(),
    confidence: z.number().nullish(),
    method: z.string().nullish(),
    flags: z.array(z.string()).optional()
});

/**
 * CategoryBreakdownResponse
 *
 * Net (without VAT) expense breakdown for one fiscal_category.
 *
 * `eligible_amount` is the figure the UI surfaces prominently — spend
 * declarable under Art. 36 LIS, i.e. ``eligibility = DEDUCIBLE``.
 * CONDICIONAL rows are *not* counted here; they live in
 * ``FinancialMetricsResponse.conditional_spend``. `total` is the full
 * bucket aggregate (DEDUCIBLE + CONDICIONAL) so the UI can show a
 * "% deducible" ratio without an extra payload.
 */
export const zCategoryBreakdownResponse = z.object({
    fiscal_category: z.string(),
    fiscal_category_name: z.string(),
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    percentage: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    eligible_count: z.int(),
    eligible_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_count: z.int()
});

/**
 * CertificateFields
 *
 * Extracted fields for certificates (certificado).
 */
export const zCertificateFields = z.object({
    issuing_authority: z.string().nullable(),
    certificate_type: z.string().nullable(),
    modelo_number: z.string().nullable(),
    tax_period: z.string().nullable(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string().nullable(),
    subject_tax_id: z.string().nullable(),
    subject_name: z.string().nullable()
});

/**
 * ClassificationDisposition
 *
 * Human decision over a classification's content (#313) — orthogonal to
 * the machine-owned processing state in ``status``.
 *
 * Values are UPPERCASE to match the ``classification_disposition`` PG enum
 * (same convention as ``Eligibility``). Only review flows write it:
 * - NONE: no terminal human decision yet (field corrections don't count).
 * - VALIDATED: reviewer approved the row's content.
 * - REJECTED: reviewer rejected the row's content.
 * - DISCARDED: removed in favour of another copy (duplicate resolution).
 */
export const zClassificationDisposition = z.enum([
    'NONE',
    'VALIDATED',
    'REJECTED',
    'DISCARDED'
]);

/**
 * ClassificationStatus
 *
 * Status lifecycle for a document classification.
 *
 * AWAITING_UPLOAD and EXPIRED moved to SessionItemStatus.
 */
export const zClassificationStatus = z.enum([
    'queued',
    'processing',
    'classified',
    'needs_review',
    'duplicate_detected',
    'skipped',
    'failed',
    'rejected',
    'discarded',
    'split'
]);

/**
 * CompanyType
 */
export const zCompanyType = z.enum([
    'freelancer',
    'limited_company',
    'public_company',
    'association',
    'foundation',
    'cooperative',
    'other'
]);

/**
 * CompatibilityAction
 *
 * Document-generating acts a template can be checked against.
 */
export const zCompatibilityAction = z.enum([
    'financing',
    'reservation',
    'communication',
    'expense_audit'
]);

/**
 * ConciliationMetricsResponse
 */
export const zConciliationMetricsResponse = z.object({
    invoices_count: z.int(),
    invoices_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    payment_proofs_count: z.int(),
    payment_proofs_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * ConsolidationBucketPreviewResponse
 *
 * One bucket row in the sync modal.
 *
 * Both amounts are GROSS EXPENSE — apples-to-apples with the quota's
 * cost_breakdown so estimated vs consolidated are directly comparable.
 * Deduction figures are surfaced at the aggregate level below.
 */
export const zConsolidationBucketPreviewResponse = z.object({
    bucket: z.string(),
    estimated_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    consolidated_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    classification_count: z.int(),
    classification_ids: z.array(z.int())
});

/**
 * ConsolidationCommitRequest
 *
 * POST /beneficiaries/{uuid}/fiscal-quotas/{fiscal_quota_uuid}/consolidate body.
 */
export const zConsolidationCommitRequest = z.object({
    breakdown_overrides: z.record(z.string(), z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ])).nullish(),
    comment: z.string().max(1000).nullish()
});

/**
 * ContractDisplayStatus
 *
 * Contract list status — a merge of the signature and review workflows.
 *
 * A contract row shows its signature status, except that a signed document
 * whose review was approved collapses to APPROVED. Rows without a signature
 * fall back to their review status. The union of both enums is therefore the
 * real domain of this field; SignatureStatus alone is not.
 */
export const zContractDisplayStatus = z.enum([
    'pending',
    'sent',
    'signed',
    'declined',
    'voided',
    'failed',
    'approved',
    'rejected'
]);

/**
 * ContractFields
 *
 * Extracted fields for contract documents (contrato).
 */
export const zContractFields = z.object({
    parties: z.array(z.string()),
    service_description: z.string().nullable(),
    total_fee: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string().nullable(),
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    payment_terms: z.string().nullable()
});

/**
 * ContractStatsResponse
 *
 * Contract statistics response.
 */
export const zContractStatsResponse = z.object({
    total_contracts: z.int(),
    by_status: z.record(z.string(), z.int()),
    by_type: z.record(z.string(), z.int())
});

/**
 * ContractsMetricsResponse
 *
 * Response model for contracts metrics.
 */
export const zContractsMetricsResponse = z.object({
    total: z.int(),
    sent: z.int(),
    pending_signatures: z.int(),
    completed: z.int()
});

/**
 * CreateUploadSessionRequest
 *
 * Request to create a bulk upload session with signed URLs.
 */
export const zCreateUploadSessionRequest = z.object({
    filenames: z.array(z.string()).min(1).max(10000),
    fiscal_quota_uuid: z.uuid(),
    deferred: z.boolean().optional().default(false)
});

/**
 * DescriptionBlockPreviewResponse
 */
export const zDescriptionBlockPreviewResponse = z.object({
    kind: z.literal('description'),
    title: z.string(),
    subtitle: z.string(),
    description: z.string(),
    rows: z.array(z.tuple([z.string(), z.string()]))
});

/**
 * Destinatario
 *
 * Recipient of the invoice line (who the cost is for). 9 closed values.
 *
 * Source-of-truth lives in
 * `services/classifier/app/domain/eligibility/destinatario.py`. This is a
 * mirror copy — the contract test pins they stay in sync.
 */
export const zDestinatario = z.enum([
    'artista_nombrado',
    'artistas_genericos',
    'crew',
    'agencia_management',
    'proveedor_externo',
    'estructura_admin',
    'local_explotacion',
    'invitado_promocional',
    'publico'
]);

/**
 * DocumentDownloadResponse
 *
 * Signed URL delivery — used by all download endpoints.
 */
export const zDocumentDownloadResponse = z.object({
    signed_url: z.string(),
    filename: z.string(),
    content_type: z.string(),
    size_bytes: z.int()
});

/**
 * DocumentStatus
 *
 * Document status — only tracks the file lifecycle.
 * Signature, review, and classification statuses live in their satellite domains.
 */
export const zDocumentStatus = z.enum(['active', 'archived']);

/**
 * DocumentUpdateRequest
 *
 * Request body for PATCH /{entity}/{id}/documents/{doc_id}.
 *
 * Fields follow a "send only what you want to change" contract: the
 * endpoint relies on ``model_fields_set`` (via :meth:`to_update_dict`)
 * to tell apart "not provided" from "explicitly set to null". So:
 *
 * - omit ``fiscal_quota_uuid`` → quota link stays as is.
 * - ``fiscal_quota_uuid=null`` → clear the link (move to "General").
 * - ``share_with_investors=false`` while currently shared → revoke the
 * grant for ``BENEFICIARY_INVESTORS``.
 * - ``share_with_investors=true`` while unscoped → rejected; sharing
 * requires the doc to be linked to a quota.
 */
export const zDocumentUpdateRequest = z.object({
    title: z.string().nullish(),
    description: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    share_with_investors: z.boolean().nullish()
});

/**
 * DownloadAeatDocumentsRequest
 *
 * Request model for downloading AEAT documents as ZIP
 */
export const zDownloadAeatDocumentsRequest = z.object({
    include_certificate: z.boolean().optional().default(false),
    certificate_type: z.enum(['inaem', 'icaa']).nullish(),
    include_communication_contract: z.boolean().optional().default(true),
    include_financing_contract: z.boolean().optional().default(true)
});

/**
 * DuplicateMatchData
 *
 * A single duplicate match reference.
 */
export const zDuplicateMatchData = z.object({
    classification_uuid: z.string(),
    match_type: z.string(),
    matched_fields: z.array(z.string()).optional()
});

/**
 * DuplicateDetectionData
 *
 * Duplicate detection results from the classifier pipeline.
 */
export const zDuplicateDetectionData = z.object({
    has_duplicates: z.boolean().optional().default(false),
    matches: z.array(zDuplicateMatchData).optional()
});

/**
 * DuplicateTemplateRequest
 *
 * Request schema for duplicating an existing template.
 */
export const zDuplicateTemplateRequest = z.object({
    name: z.string().nullish(),
    description: z.string().nullish()
});

/**
 * Eligibility
 *
 * F3 deterministic resolver output — the 3-state headline decision.
 *
 * Values are UPPERCASE to match the `document_classifications.eligibility`
 * PG enum (`eligibility_3state` type). The classifier writes
 * `Eligibility.<name>` from `services/classifier/app/domain/eligibility/`
 * into this column; the API reads it back as-is.
 *
 * The API cannot import the classifier's enum directly (services/A cannot
 * import services/B per CLAUDE.md). The contract test in
 * `tests/integration/test_classifier_api_contract.py` pins that the two
 * sides stay in sync.
 */
export const zEligibility = z.enum([
    'DEDUCIBLE',
    'CONDICIONAL',
    'NO_DEDUCIBLE'
]);

/**
 * EligibilityDecisionResponse
 *
 * F3 deterministic resolver output, surfaced alongside the legacy
 * `qualification` block until F5 contract phase drops the JSONB mirror.
 *
 * All fields except `eligibility` are nullable — the resolver does not
 * always have enough context to populate every column (e.g. fallback
 * decisions skip the rule_id / rationale path).
 */
export const zEligibilityDecisionResponse = z.object({
    eligibility: zEligibility,
    operational_category: z.string().nullish(),
    destinatario: zDestinatario.nullish(),
    destinatario_state: z.enum([
        'confident',
        'uncertain',
        'unknown'
    ]).nullish(),
    destinatario_confidence: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    fiscal_category: z.string().nullish(),
    fiscal_subcategory: z.string().nullish(),
    rule_id: z.string().nullish(),
    rationale: z.string().nullish(),
    review_question: z.string().nullish(),
    missing_evidences: z.array(z.string()).optional(),
    evidence_context: z.record(z.string(), z.unknown()).nullish(),
    deductible_base_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    ruleset_version: z.string().nullish(),
    llm_explanation: z.string().nullish()
});

/**
 * EligibilityType
 *
 * How an expense qualifies for fiscal deduction.
 *
 * Legacy F0/F1/F2 enum kept until F5 contract phase drops the JSONB
 * qualification mirror. New code reads `Eligibility` (F3 3-state) instead.
 */
export const zEligibilityType = z.enum([
    'automatic',
    'conditional',
    'never'
]);

/**
 * EntityStatus
 */
export const zEntityStatus = z.enum(['active', 'inactive']);

/**
 * EntityType
 *
 * Entity types that can have documents - used for unified documents table
 */
export const zEntityType = z.enum([
    'investment',
    'investor',
    'project',
    'organization',
    'beneficiary',
    'beneficiary_bank_account',
    'beneficiary_legal_representative',
    'user',
    'classification',
    'fiscal_quota'
]);

/**
 * ErrorResponse
 */
export const zErrorResponse = z.object({
    error: z.string(),
    message: z.string(),
    trace_id: z.string().nullish(),
    request_id: z.string().nullish(),
    details: z.record(z.string(), z.unknown()).nullish()
});

/**
 * ExportColumnResponse
 *
 * One selectable column surfaced to the export modal.
 */
export const zExportColumnResponse = z.object({
    key: z.string(),
    label: z.string(),
    default_selected: z.boolean()
});

/**
 * ExportFilterChipResponse
 *
 * One active filter rendered as a chip in the modal.
 */
export const zExportFilterChipResponse = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    value_label: z.string()
});

/**
 * ExportFormat
 *
 * Supported export file formats.
 */
export const zExportFormat = z.enum([
    'csv',
    'xlsx',
    'json'
]);

/**
 * ExportPreviewResponse
 *
 * Snapshot returned to the export modal before the user confirms.
 */
export const zExportPreviewResponse = z.object({
    count: z.int().gte(0),
    columns: z.array(zExportColumnResponse),
    filters: z.array(zExportFilterChipResponse),
    available_formats: z.array(zExportFormat)
});

/**
 * ExtendReservationRequest
 *
 * Request model for extending investment reservation
 */
export const zExtendReservationRequest = z.object({
    new_expires_at: z.iso.datetime()
});

/**
 * ExtendReservationResponse
 *
 * Response model for extend reservation action
 */
export const zExtendReservationResponse = z.object({
    extension_granted: z.boolean(),
    new_expires_at: z.iso.datetime(),
    extended_at: z.iso.datetime()
});

/**
 * FieldLockInput
 *
 * One ground-truth lock the user asserts for a reprocess.
 */
export const zFieldLockInput = z.object({
    field_key: z.string().max(50),
    value: z.string().min(1).max(200)
});

/**
 * FieldLockResponse
 *
 * A sticky ground-truth lock currently set on a classification field.
 */
export const zFieldLockResponse = z.object({
    field_key: z.string(),
    value: z.string(),
    created_by_id: z.int().nullish(),
    created_at: z.iso.datetime().nullish()
});

/**
 * FieldResponse
 *
 * Response schema for a single scalar field.
 */
export const zFieldResponse = z.object({
    key: z.string(),
    description: z.string(),
    example: z.string(),
    data_type: z.enum([
        'string',
        'currency',
        'currency_with_words',
        'date',
        'number',
        'percentage'
    ]).default('string')
});

/**
 * FieldGroupResponse
 *
 * Response schema for a group of fields.
 */
export const zFieldGroupResponse = z.object({
    group: z.string(),
    items: z.array(zFieldResponse)
});

/**
 * FiltersAppliedResponse
 *
 * Response model for filters applied.
 */
export const zFiltersAppliedResponse = z.object({
    beneficiary_uuid: z.uuid().nullish(),
    beneficiary_name: z.string().nullish(),
    fiscal_year: z.int()
});

/**
 * DashboardMetadataResponse
 *
 * Response model for dashboard metadata.
 */
export const zDashboardMetadataResponse = z.object({
    filters_applied: zFiltersAppliedResponse,
    generated_at: z.iso.datetime()
});

/**
 * FinancialMetricsResponse
 */
export const zFinancialMetricsResponse = z.object({
    total_spend: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    deductible_spend: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    non_deductible_spend: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    conditional_spend: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    conditional_count: z.int(),
    estimated_savings: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * FiscalQuotaCreateRequest
 *
 * Request model for creating a fiscal quota
 */
export const zFiscalQuotaCreateRequest = z.object({
    fiscal_year: z.int(),
    estimated_deduction: z.string(),
    comments: z.string().nullish(),
    default_profitability_percentage: z.string(),
    deduction_rate: z.string().nullish(),
    cost_breakdown: z.record(z.string(), z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ])).nullish(),
    total_budget: z.string().nullish(),
    total_eligible_cost: z.string().nullish(),
    deduction_base: z.string().nullish(),
    total_generated_deduction: z.string().nullish(),
    own_contribution: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    subsidies: z.array(z.record(z.string(), z.unknown())).nullish()
});

/**
 * FiscalQuotaUpdateRequest
 *
 * Request model for updating a fiscal quota
 */
export const zFiscalQuotaUpdateRequest = z.object({
    estimated_deduction: z.string().nullish(),
    comments: z.string().nullish(),
    default_profitability_percentage: z.string().nullish(),
    deduction_rate: z.string().nullish(),
    cost_breakdown: z.record(z.string(), z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ])).nullish(),
    total_budget: z.string().nullish(),
    total_eligible_cost: z.string().nullish(),
    deduction_base: z.string().nullish(),
    total_generated_deduction: z.string().nullish(),
    own_contribution: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    subsidies: z.array(z.record(z.string(), z.unknown())).nullish()
});

/**
 * FiscalYearAggregateResponse
 *
 * Aggregate metrics across all fiscal quotas for a given (beneficiary, year).
 */
export const zFiscalYearAggregateResponse = z.object({
    total_estimated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_used: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    utilization_percentage: z.number(),
    reserved_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    completed_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_used_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    available_estimate_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    capacity_estimate_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_budget: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_eligible_cost: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_deduction_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_generated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_own_contribution: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable()
});

/**
 * GenerateExpenseAuditRequest
 *
 * Body for ``POST /fiscal-quotas/{uuid}/actions/generate-expense-audit``.
 */
export const zGenerateExpenseAuditRequest = z.object({
    template_uuid: z.uuid()
});

/**
 * GeneratedSQLResponse
 *
 * Response DTO for generated SQL.
 */
export const zGeneratedSqlResponse = z.object({
    sql: z.string(),
    tables_used: z.array(z.string()),
    explanation: z.string().nullable()
});

/**
 * HubBankInfoResponse
 *
 * Bank info for payment.
 */
export const zHubBankInfoResponse = z.object({
    iban: z.string(),
    bank_name: z.string().nullable(),
    has_certificate: z.boolean().default(false),
    certificate_url: z.string().nullable()
});

/**
 * HubExpiringReservationResponse
 *
 * Reservation expiring soon.
 */
export const zHubExpiringReservationResponse = z.object({
    investment_uuid: z.uuid(),
    beneficiary_name: z.string(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    expires_at: z.iso.datetime(),
    days_remaining: z.int()
});

/**
 * HubPendingContractResponse
 *
 * Contract pending investor signature.
 */
export const zHubPendingContractResponse = z.object({
    investment_uuid: z.uuid(),
    beneficiary_name: z.string(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    contract_type: z.enum([
        'reservation',
        'financing',
        'communication'
    ]),
    document_uuid: z.uuid(),
    sent_at: z.iso.datetime().nullable()
});

/**
 * HubPendingPaymentResponse
 *
 * Investment pending payment upload.
 */
export const zHubPendingPaymentResponse = z.object({
    investment_uuid: z.uuid(),
    beneficiary_name: z.string(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    bank_info: zHubBankInfoResponse
});

/**
 * HubPendingActionsResponse
 *
 * All pending actions for an investor.
 */
export const zHubPendingActionsResponse = z.object({
    contracts_to_sign: z.array(zHubPendingContractResponse).default([]),
    pending_payments: z.array(zHubPendingPaymentResponse).default([]),
    expiring_reservations: z.array(zHubExpiringReservationResponse).default([]),
    total_count: z.int().default(0),
    has_actions: z.boolean().default(false)
});

/**
 * HubSigningUrlRequest
 *
 * Request for embedded signing URL.
 */
export const zHubSigningUrlRequest = z.object({
    return_url: z.string()
});

/**
 * HubSigningUrlResponse
 *
 * Response with signing URL (redirect-based or embeddable).
 */
export const zHubSigningUrlResponse = z.object({
    signing_url: z.string().nullable(),
    embed_src: z.string().nullable(),
    expires_in_seconds: z.int().default(300)
});

/**
 * HubStatus
 *
 * Hub access status for an investor.
 * - none: No invitation sent, no hub access
 * - pending: Invitation sent, waiting for investor to accept
 * - active: Investor has accepted invitation and has hub access
 */
export const zHubStatus = z.enum([
    'none',
    'pending',
    'active'
]);

/**
 * ImageBlockPreviewResponse
 */
export const zImageBlockPreviewResponse = z.object({
    kind: z.literal('image')
});

/**
 * InvestmentCapacityMetricsResponse
 *
 * Response model for investment capacity metrics from beneficiary perspective.
 */
export const zInvestmentCapacityMetricsResponse = z.object({
    estimated_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullish(),
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    estimated_available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullish()
});

/**
 * InvestmentEventType
 *
 * Investment event types - actions/events that occur during lifecycle
 */
export const zInvestmentEventType = z.enum([
    'created',
    'proposal_sent',
    'reservation_contract_sent',
    'financing_contract_sent',
    'communication_contract_sent',
    'contract_signed',
    'invoice_sent',
    'reservation_created',
    'reservation_extended',
    'reservation_expiry_set',
    'reservation_released',
    'reservation_expired',
    'quota_reserved',
    'quota_released',
    'quota_expired',
    'payment_received',
    'payment_confirmed',
    'payment_reversed',
    'tax_submitted',
    'tax_accepted',
    'tax_rejected',
    'document_uploaded',
    'document_downloaded',
    'document_approved',
    'document_rejected',
    'contract_voided',
    'contract_declined',
    'contract_delivery_failed',
    'state_changed',
    'amount_updated',
    'notes_updated',
    'beneficiary_changed',
    'return_percentage_updated',
    'cancelled',
    'rejected'
]);

/**
 * InvestmentMetricsResponse
 *
 * Response model for investment metrics
 */
export const zInvestmentMetricsResponse = z.object({
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    total_used: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available_estimate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable(),
    capacity_estimate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable()
});

/**
 * InvestmentProposalCreateRequest
 *
 * Request model for creating a new investment proposal
 */
export const zInvestmentProposalCreateRequest = z.object({
    investor_uuid: z.uuid(),
    fiscal_year: z.int(),
    taxable_base: z.string(),
    tax_rate: z.union([
        z.number().gt(0).lte(100),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]),
    expires_at: z.iso.datetime()
});

/**
 * InvestmentProposalStatus
 *
 * Investment proposal status enum
 */
export const zInvestmentProposalStatus = z.enum([
    'pending',
    'accepted',
    'rejected'
]);

/**
 * InvestmentState
 *
 * Investment state enum - represents current state of investment
 */
export const zInvestmentState = z.enum([
    'prospect',
    'reserved',
    'financing',
    'funded',
    'declared',
    'cancelled',
    'expired',
    'rejected'
]);

/**
 * ContractInvestmentContext
 *
 * Investment context embedded in contract detail response.
 */
export const zContractInvestmentContext = z.object({
    uuid: z.uuid(),
    state: zInvestmentState,
    investor: z.record(z.string(), z.unknown()),
    beneficiary: z.record(z.string(), z.unknown())
});

/**
 * HubInvestmentDetailResponse
 *
 * Detailed Hub investment view.
 */
export const zHubInvestmentDetailResponse = z.object({
    uuid: z.uuid(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    return_percentage: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    currency: z.string().default('EUR'),
    state: zInvestmentState,
    beneficiary_name: z.string(),
    project_name: z.string().nullable(),
    created_at: z.iso.datetime().nullable(),
    updated_at: z.iso.datetime().nullable()
});

/**
 * HubInvestmentResponse
 *
 * Investment for Hub - excludes notes, audit fields, internal IDs.
 */
export const zHubInvestmentResponse = z.object({
    uuid: z.uuid(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    return_percentage: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    currency: z.string().default('EUR'),
    state: zInvestmentState,
    beneficiary_name: z.string(),
    project_name: z.string().nullable()
});

/**
 * HubDashboardSummary
 *
 * Dashboard summary for Hub - investment statistics + recent activity.
 */
export const zHubDashboardSummary = z.object({
    total_investments: z.int().default(0),
    total_invested_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).default('0.00'),
    pending_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).default('0.00'),
    confirmed_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).default('0.00'),
    recent_investments: z.array(zHubInvestmentResponse).default([])
});

/**
 * InvestmentCreateRequest
 *
 * Request model for creating a new investment
 */
export const zInvestmentCreateRequest = z.object({
    investor_uuid: z.uuid(),
    fiscal_quota_uuid: z.uuid(),
    amount: z.string(),
    return_percentage: z.string(),
    state: zInvestmentState.optional().default('prospect'),
    notes: z.string().max(1000).nullish()
});

/**
 * InvestmentSummary
 *
 * Investment summary data for investor responses
 */
export const zInvestmentSummary = z.object({
    number_investments: z.int().default(0),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    reserved_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    confirmed_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestmentUpdateRequest
 *
 * Request model for updating investment via PATCH
 */
export const zInvestmentUpdateRequest = z.object({
    amount: z.string().nullish(),
    return_percentage: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    state: zInvestmentState.nullish(),
    notes: z.string().max(1000).nullish()
});

/**
 * InvestmentsSummaryResponse
 *
 * Response model for investments summary.
 */
export const zInvestmentsSummaryResponse = z.object({
    total_count: z.int(),
    total_volume: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestorHubRegistrationData
 *
 * Simplified registration for investor hub invitations.
 * Only password required - all other user data (name, language) is pulled
 * from the Investor record automatically.
 */
export const zInvestorHubRegistrationData = z.object({
    type: z.literal('investor_hub_registration'),
    password: z.string().min(8)
});

/**
 * InvestorLegalRepresentativeCreateRequest
 *
 * Request model for creating an investor legal representative
 */
export const zInvestorLegalRepresentativeCreateRequest = z.object({
    first_name: z.string().max(120),
    last_name: z.string().max(160),
    dni: z.string().max(20),
    position: z.string().max(160).nullish(),
    phone: z.string().max(50).nullish(),
    email: z.email().nullish(),
    is_primary: z.boolean().optional().default(true),
    is_signer: z.boolean().optional().default(true)
});

/**
 * InvestorLegalRepresentativeResponse
 *
 * Response model for investor legal representative data
 */
export const zInvestorLegalRepresentativeResponse = z.object({
    uuid: z.uuid(),
    first_name: z.string(),
    last_name: z.string(),
    full_name: z.string(),
    dni: z.string(),
    position: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    is_primary: z.boolean(),
    is_signer: z.boolean(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * InvestorLegalRepresentativeUpdateRequest
 *
 * Request model for updating an investor legal representative
 */
export const zInvestorLegalRepresentativeUpdateRequest = z.object({
    first_name: z.string().max(120).nullish(),
    last_name: z.string().max(160).nullish(),
    dni: z.string().max(20).nullish(),
    position: z.string().max(160).nullish(),
    phone: z.string().max(50).nullish(),
    email: z.email().nullish(),
    is_primary: z.boolean().nullish(),
    is_signer: z.boolean().nullish()
});

/**
 * InvestorMusicDeductionSummaryResponse
 *
 * Response for /investors/{uuid}/music-deduction-summary.
 *
 * Amount invested and deduction already transferred to the investor across
 * music quotas of the fiscal year. Art. 36.3 LIS caps the music deduction at
 * 500.000 € per contribuyente and período impositivo — the platform only sees
 * its own investments, so the FE renders this as an advisory warning, never
 * a block.
 */
export const zInvestorMusicDeductionSummaryResponse = z.object({
    fiscal_year: z.int(),
    invested_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    transferred_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    legal_cap: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestorStatus
 *
 * Investor status enum for domain layer
 */
export const zInvestorStatus = z.enum([
    'pending',
    'active',
    'inactive'
]);

/**
 * InvestorType
 *
 * Investor type enum for domain layer
 */
export const zInvestorType = z.enum(['individual', 'company']);

/**
 * HubInvestorProfileSummary
 *
 * Lightweight investor profile for profile switcher.
 * Contains only essential fields needed to identify and select a profile.
 */
export const zHubInvestorProfileSummary = z.object({
    uuid: z.uuid(),
    type: zInvestorType,
    display_name: z.string(),
    email: z.string().nullish()
});

/**
 * HubProfilesResponse
 *
 * Response containing all investor profiles available to the user.
 * Used by the Hub profile switcher.
 */
export const zHubProfilesResponse = z.object({
    profiles: z.array(zHubInvestorProfileSummary),
    count: z.int()
});

/**
 * InvestorOperationResponse
 *
 * Response for operations that work on both individual and company investors
 */
export const zInvestorOperationResponse = z.object({
    uuid: z.uuid(),
    type: zInvestorType,
    status: zInvestorStatus,
    display_name: z.string()
});

/**
 * InvestorProjectSummary
 *
 * Investor summary data specifically for project detail responses.
 * Contains only essential investor info and investment details for the project.
 */
export const zInvestorProjectSummary = z.object({
    uuid: z.uuid(),
    display_name: z.string(),
    type: zInvestorType,
    investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    invested_at: z.iso.datetime(),
    status: zInvestmentState
});

/**
 * InvestorSummary
 *
 * Summary investor data for nested responses
 */
export const zInvestorSummary = z.object({
    uuid: z.uuid(),
    type: zInvestorType,
    status: zInvestorStatus,
    display_name: z.string(),
    email: z.string().nullable(),
    tax_identifier: z.string().nullable(),
    city: z.string().nullable()
});

/**
 * InvestorTypeBreakdownResponse
 *
 * Breakdown of investors by type
 */
export const zInvestorTypeBreakdownResponse = z.object({
    individual: z.int(),
    company: z.int()
});

/**
 * InvestorMetricsResponse
 *
 * Response for /investors/metrics endpoint
 */
export const zInvestorMetricsResponse = z.object({
    total_count: z.int(),
    by_type: zInvestorTypeBreakdownResponse,
    potential_capacity: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    committed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestorTypeMetricsResponse
 *
 * Response model for investor type metrics.
 */
export const zInvestorTypeMetricsResponse = z.object({
    count: z.int(),
    avg_investment: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestorsByTypeResponse
 *
 * Response model for investor breakdown by type.
 */
export const zInvestorsByTypeResponse = z.object({
    individual: zInvestorTypeMetricsResponse,
    company: zInvestorTypeMetricsResponse
});

/**
 * InvestorBreakdownResponse
 *
 * Response model for investor breakdown.
 */
export const zInvestorBreakdownResponse = z.object({
    total_count: z.int(),
    by_type: zInvestorsByTypeResponse
});

/**
 * InvestorCapacityMetricsResponse
 *
 * Response model for investor capacity metrics.
 * Business concept: "Capacidad de Inversión" (Demand Side)
 */
export const zInvestorCapacityMetricsResponse = z.object({
    potential_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    committed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    investors: zInvestorBreakdownResponse
});

/**
 * InvitationStatus
 *
 * Invitation status enum
 */
export const zInvitationStatus = z.enum([
    'pending',
    'accepted',
    'rejected',
    'expired',
    'revoked'
]);

/**
 * InvitationType
 *
 * Invitation type enum
 */
export const zInvitationType = z.enum([
    'create_org',
    'join_as_new_user',
    'join_as_existing_user'
]);

/**
 * InvoiceLineItem
 */
export const zInvoiceLineItem = z.object({
    description: z.string().nullish(),
    quantity: z.number().nullish(),
    unit_price: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish()
});

/**
 * IvaBreakdownEntry
 *
 * One IVA rate block of a multi-rate invoice (Base/Tipo/Cuota line).
 */
export const zIvaBreakdownEntry = z.object({
    rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable()
});

/**
 * InvoiceFields
 *
 * Extracted fields for invoices (factura, proforma, presupuesto, albaran).
 */
export const zInvoiceFields = z.object({
    invoice_number: z.string().nullable(),
    invoice_date: z.string().nullable(),
    due_date: z.string().nullable(),
    supplier_name: z.string().nullable(),
    supplier_tax_id: z.string().nullable(),
    supplier_address: z.string().nullable(),
    receiver_name: z.string().nullable(),
    receiver_tax_id: z.string().nullable(),
    receiver_address: z.string().nullable(),
    subtotal: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string().nullable().default('EUR'),
    iva_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    iva_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    iva_breakdown: z.array(zIvaBreakdownEntry),
    irpf_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    irpf_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    concept_summary: z.string().nullable(),
    line_items: z.array(zInvoiceLineItem),
    beneficiary_name: z.string().nullable()
});

/**
 * IvaRateBreakdownResponse
 */
export const zIvaRateBreakdownResponse = z.object({
    rate: z.string(),
    taxable_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    invoice_count: z.int()
});

/**
 * ListBlockPreviewResponse
 */
export const zListBlockPreviewResponse = z.object({
    kind: z.literal('list'),
    sample_items: z.array(z.string())
});

/**
 * ListResponse[BeneficiaryBankAccountResponse]
 */
export const zListResponseBeneficiaryBankAccountResponse = z.object({
    items: z.array(zBeneficiaryBankAccountResponse),
    total_count: z.int()
});

/**
 * ListResponse[BeneficiaryLegalRepresentativeResponse]
 */
export const zListResponseBeneficiaryLegalRepresentativeResponse = z.object({
    items: z.array(zBeneficiaryLegalRepresentativeResponse),
    total_count: z.int()
});

/**
 * ListResponse[InvestorLegalRepresentativeResponse]
 */
export const zListResponseInvestorLegalRepresentativeResponse = z.object({
    items: z.array(zInvestorLegalRepresentativeResponse),
    total_count: z.int()
});

/**
 * LockableFieldResponse
 *
 * A field the reprocess modal may offer to lock, with its current value.
 */
export const zLockableFieldResponse = z.object({
    field_key: z.string(),
    label: z.string(),
    value_type: z.enum([
        'document_type',
        'amount',
        'date',
        'text'
    ]),
    current_value: z.string().nullable()
});

/**
 * ClassificationLocksResponse
 *
 * Everything the reprocess modal needs: the active locks plus the
 * lockable-field catalogue for this document's type.
 */
export const zClassificationLocksResponse = z.object({
    locks: z.array(zFieldLockResponse),
    lockable_fields: z.array(zLockableFieldResponse)
});

/**
 * MembershipJoinData
 *
 * Completion data for JOIN_AS_EXISTING_USER invitations - creates membership only.
 * For role=INVESTOR, the investor link is created automatically using the
 * investor_uuid embedded in the signed invitation token.
 */
export const zMembershipJoinData = z.object({
    type: z.literal('membership_join')
});

/**
 * MessageResponse
 *
 * Simple message response for admin actions.
 */
export const zMessageResponse = z.object({
    message: z.string()
});

/**
 * ModuleCode
 *
 * Available vendible modules.
 */
export const zModuleCode = z.enum([
    'ai_agent',
    'invoice_classification',
    'signing'
]);

/**
 * ModuleEntitlementResponse
 *
 * Response DTO for a module entitlement.
 */
export const zModuleEntitlementResponse = z.object({
    module_code: z.string(),
    is_active: z.boolean(),
    config: z.record(z.string(), z.unknown()),
    activated_at: z.iso.datetime().nullable(),
    expires_at: z.iso.datetime().nullable()
});

/**
 * ListResponse[ModuleEntitlementResponse]
 */
export const zListResponseModuleEntitlementResponse = z.object({
    items: z.array(zModuleEntitlementResponse),
    total_count: z.int()
});

/**
 * MonthlyBreakdownResponse
 */
export const zMonthlyBreakdownResponse = z.object({
    month: z.int(),
    deductible: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    non_deductible: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * NotificationStatsResponse
 *
 * Schema for notification statistics
 */
export const zNotificationStatsResponse = z.object({
    total_count: z.int(),
    unread_count: z.int(),
    read_count: z.int()
});

/**
 * NotificationType
 */
export const zNotificationType = z.enum([
    'system',
    'document',
    'investment',
    'project'
]);

/**
 * OrganizationCreateRequest
 *
 * Request schema for creating an organization
 */
export const zOrganizationCreateRequest = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100),
    email: z.email().nullish(),
    phone: z.string().max(20).nullish(),
    website: z.string().max(200).nullish(),
    address: z.string().max(200).nullish(),
    city: z.string().max(50).nullish(),
    state: z.string().max(50).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    fiscal_id: z.string().max(50).nullish(),
    tax_advisor_id: z.string().max(50).nullish(),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(160).nullish(),
    legal_rep_national_id: z.string().max(32).nullish(),
    legal_rep_email: z.email().nullish(),
    logo: z.string().nullish()
});

/**
 * OrganizationResponse
 *
 * Response schema for organization data
 */
export const zOrganizationResponse = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100),
    email: z.email().nullish(),
    phone: z.string().max(20).nullish(),
    website: z.string().max(200).nullish(),
    address: z.string().max(200).nullish(),
    city: z.string().max(50).nullish(),
    state: z.string().max(50).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    fiscal_id: z.string().max(50).nullish(),
    tax_advisor_id: z.string().max(50).nullish(),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(160).nullish(),
    legal_rep_national_id: z.string().max(32).nullish(),
    legal_rep_email: z.email().nullish(),
    uuid: z.uuid(),
    logo_signed_url: z.string().nullish(),
    is_active: z.boolean(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime().nullish()
});

/**
 * OrganizationRole
 *
 * Organization roles enum
 */
export const zOrganizationRole = z.enum([
    'owner',
    'employee',
    'beneficiary',
    'investor'
]);

/**
 * AdminInvitationCreateRequest
 *
 * Request schema for admin-initiated invitations.
 * The backend resolves 'name' from user record (if exists) and infers invitation type automatically.
 */
export const zAdminInvitationCreateRequest = z.object({
    organization_uuid: z.uuid(),
    email: z.email(),
    role: zOrganizationRole,
    message: z.string().nullish()
});

/**
 * InvitationCreateRequest
 *
 * Request schema for creating an organization invitation
 */
export const zInvitationCreateRequest = z.object({
    email: z.email(),
    name: z.string(),
    role: zOrganizationRole,
    type: zInvitationType
});

/**
 * InvitationResponse
 *
 * Response schema for invitation data
 */
export const zInvitationResponse = z.object({
    uuid: z.uuid(),
    email: z.string(),
    role: zOrganizationRole,
    status: zInvitationStatus,
    invitation_type: zInvitationType,
    expires_at: z.iso.datetime(),
    accepted_at: z.iso.datetime().nullish(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    organization_name: z.string().nullish(),
    investor_name: z.string().nullish(),
    already_completed: z.boolean().optional().default(false)
});

/**
 * ListResponse[InvitationResponse]
 */
export const zListResponseInvitationResponse = z.object({
    items: z.array(zInvitationResponse),
    total_count: z.int()
});

/**
 * MembershipUpdateRequest
 *
 * Request model for updating membership data
 */
export const zMembershipUpdateRequest = z.object({
    role: zOrganizationRole.nullish(),
    is_active: z.boolean().nullish()
});

/**
 * OrganizationSummary
 *
 * Summary organization data for nested responses
 */
export const zOrganizationSummary = z.object({
    uuid: z.uuid(),
    name: z.string(),
    slug: z.string(),
    logo_signed_url: z.string().nullish(),
    is_active: z.boolean()
});

/**
 * InvestmentProposalResponse
 *
 * Response model for investment proposal
 */
export const zInvestmentProposalResponse = z.object({
    uuid: z.uuid(),
    investor: zInvestorSummary,
    organization: zOrganizationSummary,
    fiscal_year: z.int(),
    taxable_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    tax_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    status: zInvestmentProposalStatus,
    expires_at: z.iso.datetime(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * OrganizationUpdateRequest
 *
 * Request schema for updating an organization
 */
export const zOrganizationUpdateRequest = z.object({
    name: z.string().min(1).max(100).nullish(),
    logo: z.string().nullish(),
    email: z.email().nullish(),
    phone: z.string().max(20).nullish(),
    website: z.string().max(200).nullish(),
    address: z.string().max(200).nullish(),
    city: z.string().max(50).nullish(),
    state: z.string().max(50).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    fiscal_id: z.string().max(50).nullish(),
    tax_advisor_id: z.string().max(50).nullish(),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(160).nullish(),
    legal_rep_national_id: z.string().max(32).nullish(),
    legal_rep_email: z.email().nullish(),
    is_active: z.boolean().nullish()
});

/**
 * PaginatedResponse[HubInvestmentResponse]
 */
export const zPaginatedResponseHubInvestmentResponse = z.object({
    items: z.array(zHubInvestmentResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * PaginatedResponse[InvestmentProposalResponse]
 */
export const zPaginatedResponseInvestmentProposalResponse = z.object({
    items: z.array(zInvestmentProposalResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * PaymentFields
 *
 * Extracted fields for payment receipts (comprobante_pago).
 */
export const zPaymentFields = z.object({
    amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string().nullable(),
    transfer_date: z.string().nullable(),
    beneficiary_name: z.string().nullable(),
    beneficiary_tax_id: z.string().nullable(),
    ordering_party: z.string().nullable(),
    bank_name: z.string().nullable(),
    invoice_reference: z.string().nullable(),
    transfer_reference: z.string().nullable(),
    concept: z.string().nullable()
});

/**
 * PayrollFields
 *
 * Extracted fields for payroll documents (nomina).
 */
export const zPayrollFields = z.object({
    company_name: z.string().nullable(),
    company_tax_id: z.string().nullable(),
    company_ccc: z.string().nullable(),
    worker_name: z.string().nullable(),
    worker_tax_id: z.string().nullable(),
    worker_nss: z.string().nullable(),
    worker_position: z.string().nullable(),
    worker_category: z.string().nullable(),
    worker_start_date: z.string().nullable(),
    contract_type: z.string().nullable(),
    period_month: z.int().nullable(),
    period_year: z.int().nullable(),
    period_start: z.string().nullable(),
    period_end: z.string().nullable(),
    payroll_type: z.string().nullable(),
    base_salary: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    seniority_bonus: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    extra_payments: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    overtime_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    other_earnings: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_earnings: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    ss_worker_contribution: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    irpf_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    irpf_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    other_deductions: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_deductions: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    net_salary: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    ss_employer_contribution: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    coste_empresa: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string().nullable()
});

/**
 * PipelineErrorData
 *
 * Pipeline error details persisted by the classifier worker.
 */
export const zPipelineErrorData = z.object({
    failed_stage: z.string().nullish(),
    is_retriable: z.boolean().optional().default(false),
    error: z.string().nullish(),
    user_message: z.string().nullish()
});

/**
 * PipelineStateDataResponse
 *
 * Response model for pipeline data by state.
 */
export const zPipelineStateDataResponse = z.object({
    state: zInvestmentState,
    count: z.int(),
    volume: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * InvestmentsMetricsResponse
 *
 * Response model for investments metrics.
 * Business concept: "Inversión" (Transactions)
 */
export const zInvestmentsMetricsResponse = z.object({
    summary: zInvestmentsSummaryResponse,
    pipeline: z.array(zPipelineStateDataResponse)
});

/**
 * PipelineStatusBreakdownResponse
 *
 * Counts of classifications grouped by pipeline status.
 *
 * Powers the operational "Estado de clasificación" panel in the
 * beneficiary overview. Buckets:
 *
 * - ``queued`` — QUEUED, waiting to enter the pipeline (info-only)
 * - ``processing`` — PROCESSING, currently being classified (info-only)
 * - ``failed`` — FAILED (actionable: bulk reprocess)
 * - ``classified`` — CLASSIFIED (info-only)
 * - ``needs_review`` — NEEDS_REVIEW (actionable: review modal)
 * - ``duplicate_detected`` — DUPLICATE_DETECTED (actionable: open
 * listing filtered + comparison modal)
 *
 * SKIPPED / REJECTED / DISCARDED are intentionally omitted: they don't
 * appear in the operational panel and would only add noise.
 */
export const zPipelineStatusBreakdownResponse = z.object({
    queued: z.int(),
    processing: z.int(),
    failed: z.int(),
    classified: z.int(),
    needs_review: z.int(),
    duplicate_detected: z.int()
});

/**
 * PreviewCommunicationContractRequest
 *
 * Request model for previewing communication contract.
 *
 * Mirrors ``SendCommunicationContractRequest``: the template decides
 * which annexes are attached, no request-level toggles.
 */
export const zPreviewCommunicationContractRequest = z.object({
    template_uuid: z.uuid()
});

/**
 * PreviewExpenseAuditRequest
 *
 * Body for ``POST /fiscal-quotas/{uuid}/actions/preview-expense-audit``.
 *
 * Same input as the generate request: the preview renders the exact same
 * PDF, just without persisting Document or uploading to GCS.
 */
export const zPreviewExpenseAuditRequest = z.object({
    template_uuid: z.uuid()
});

/**
 * PreviewFinancingContractRequest
 *
 * Request model for previewing financing contract
 */
export const zPreviewFinancingContractRequest = z.object({
    template_uuid: z.uuid(),
    payment_deadline: z.iso.datetime().nullish()
});

/**
 * PreviewReservationContractRequest
 *
 * Request model for previewing reservation contract
 */
export const zPreviewReservationContractRequest = z.object({
    template_uuid: z.uuid(),
    payment_deadline: z.iso.datetime().nullish()
});

/**
 * ProjectSubtype
 */
export const zProjectSubtype = z.enum([
    'feature_film',
    'short_film',
    'film',
    'documentary',
    'series',
    'animation',
    'tv_movie',
    'music_video',
    'theatre',
    'dance',
    'opera',
    'musical',
    'music_festival',
    'music_event',
    'tour',
    'concert_hall',
    'nightclub',
    'circus'
]);

/**
 * LinkedProjectSummaryResponse
 *
 * Minimal project info attached to a FiscalQuota for display contexts.
 */
export const zLinkedProjectSummaryResponse = z.object({
    uuid: z.uuid(),
    title: z.string(),
    subtype: zProjectSubtype.nullable(),
    city: z.string().nullable(),
    production_start_date: z.iso.date().nullable(),
    production_end_date: z.iso.date().nullable()
});

/**
 * FiscalQuotaSummary
 *
 * Summary model for beneficiary fiscal quota data
 */
export const zFiscalQuotaSummary = z.object({
    uuid: z.uuid(),
    fiscal_year: z.int(),
    estimated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    reserved_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    completed_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    utilization_percentage: z.number(),
    linked_projects: z.array(zLinkedProjectSummaryResponse).default([]),
    cost_breakdown: z.record(z.string(), z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)).nullable(),
    total_budget: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_eligible_cost: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    deduction_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_generated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    own_contribution: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    subsidies: z.array(z.record(z.string(), z.unknown())).nullable()
});

/**
 * ProjectBulkRow
 *
 * A single row coming from the Excel import wizard.
 *
 * Mirrors the user-editable columns of the template. Fields shared by the
 * whole import (``fiscal_quota_uuid`` and the incentive type) are not on the
 * row — the endpoint resolves the quota once and infers the incentive type
 * from the beneficiary. ``fundraising_date`` is intentionally excluded: it's
 * a back-office field, not something promoters fill in via Excel.
 */
export const zProjectBulkRow = z.object({
    title: z.string().min(1).max(255),
    subtype: zProjectSubtype,
    fiscal_year: z.int().gte(1900).lte(2100).nullish(),
    city: z.string().max(100).nullish(),
    production_start_date: z.iso.date().nullish(),
    production_end_date: z.iso.date().nullish(),
    description: z.string().max(1000).nullish()
});

/**
 * ProjectBulkCreateRequest
 *
 * Wizard payload: one fiscal quota + N project rows.
 *
 * The wizard is rooted in a fiscal quota modal, so ``fiscal_quota_uuid`` is
 * shared across all rows. Submission is atomic on the backend — if any row
 * fails validation or persistence the whole batch is rolled back.
 */
export const zProjectBulkCreateRequest = z.object({
    fiscal_quota_uuid: z.uuid(),
    projects: z.array(zProjectBulkRow).min(1).max(200)
});

/**
 * QualificationData
 *
 * Fiscal qualification result from the qualification stage.
 */
export const zQualificationData = z.object({
    eligible: z.boolean().nullish(),
    eligibility_type: zEligibilityType.nullish(),
    fiscal_category: z.string().nullish(),
    fiscal_subcategory: z.string().nullish(),
    article: z.string().nullish(),
    deduction_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    reasoning: z.string().nullish()
});

/**
 * ClassificationResultData
 *
 * Combined categorization, qualification, error, and duplicate detection results.
 */
export const zClassificationResultData = z.object({
    categorization: zCategorizationData.nullish(),
    qualification: zQualificationData.nullish(),
    pipeline_error: zPipelineErrorData.nullish(),
    duplicate_detection: zDuplicateDetectionData.nullish()
});

/**
 * QueryResultResponse
 *
 * Response DTO for query execution result.
 */
export const zQueryResultResponse = z.object({
    columns: z.array(z.string()),
    rows: z.array(z.record(z.string(), z.unknown())),
    row_count: z.int(),
    execution_time_ms: z.number()
});

/**
 * QuotaDimension
 *
 * Whether a quota is pooled across the org or applied per user.
 */
export const zQuotaDimension = z.enum(['per_org', 'per_user']);

/**
 * QuotaEnforcement
 *
 * Whether exceeding the quota blocks the action or is only observed.
 */
export const zQuotaEnforcement = z.enum(['monitor', 'enforce']);

/**
 * QuotaUnit
 *
 * Unit the quota limit is expressed in.
 */
export const zQuotaUnit = z.enum([
    'tokens',
    'count',
    'bytes'
]);

/**
 * QuotaWindow
 *
 * Time window a quota's usage is accumulated over.
 */
export const zQuotaWindow = z.enum([
    'day',
    'month',
    'rolling_30d',
    'total'
]);

/**
 * QuotaDescriptorSchema
 *
 * One quota limit on a metric — the shape stored in entitlement config.quotas.
 */
export const zQuotaDescriptorSchema = z.object({
    dimension: zQuotaDimension,
    window: zQuotaWindow,
    unit: zQuotaUnit,
    limit: z.int().gte(0),
    enforcement: zQuotaEnforcement.optional().default('monitor'),
    warning_pct: z.number().gte(0).lte(100).optional().default(80)
});

/**
 * AdminEntitlementCreateRequest
 *
 * Grant a module to an organization (platform admin).
 */
export const zAdminEntitlementCreateRequest = z.object({
    module_code: zModuleCode,
    quotas: z.record(z.string(), zQuotaDescriptorSchema).optional(),
    is_active: z.boolean().optional().default(true),
    expires_at: z.iso.datetime().nullish()
});

/**
 * AdminEntitlementUpdateRequest
 *
 * Patch an entitlement's quotas, activation, or expiry. None leaves a field unchanged.
 */
export const zAdminEntitlementUpdateRequest = z.object({
    quotas: z.record(z.string(), zQuotaDescriptorSchema).nullish(),
    is_active: z.boolean().nullish(),
    expires_at: z.iso.datetime().nullish()
});

/**
 * QuotaStateResponse
 *
 * Evaluated usage vs limit for one quota descriptor.
 */
export const zQuotaStateResponse = z.object({
    metric: z.string(),
    dimension: zQuotaDimension,
    window: zQuotaWindow,
    used: z.int(),
    limit: z.int(),
    pct_used: z.number(),
    exceeded: z.boolean(),
    warning: z.boolean(),
    enforcement: zQuotaEnforcement
});

/**
 * AdminEntitlementResponse
 *
 * Admin view of an entitlement with its evaluated quota states.
 */
export const zAdminEntitlementResponse = z.object({
    uuid: z.string(),
    module_code: z.string(),
    is_active: z.boolean(),
    config: z.record(z.string(), z.unknown()),
    activated_at: z.iso.datetime().nullable(),
    expires_at: z.iso.datetime().nullable(),
    quota_reset_at: z.iso.datetime().nullable(),
    quota_states: z.array(zQuotaStateResponse)
});

/**
 * ReclassifyClassificationRequest
 *
 * Optional body for the single reclassify endpoint.
 *
 * ``locks`` REPLACES the document's full lock set — the modal always shows
 * the true current state, so what it submits is the desired set and an
 * unchecked field is a removed lock. Omit the body entirely to reprocess
 * while preserving existing sticky locks (quick reprocess / bulk path); an
 * empty list clears all locks.
 */
export const zReclassifyClassificationRequest = z.object({
    locks: z.array(zFieldLockInput).max(20).optional()
});

/**
 * RelatedEntityType
 */
export const zRelatedEntityType = z.enum([
    'project',
    'investment',
    'document',
    'contract',
    'beneficiary'
]);

/**
 * NotificationResponse
 *
 * Schema for notification responses
 */
export const zNotificationResponse = z.object({
    title: z.string().min(1).max(255),
    body: z.string().max(1000).nullish(),
    type: zNotificationType,
    related_entity_type: zRelatedEntityType.nullish(),
    related_entity_uuid: z.uuid().nullish(),
    uuid: z.uuid(),
    is_read: z.boolean(),
    read_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime()
});

/**
 * PaginatedResponse[NotificationResponse]
 */
export const zPaginatedResponseNotificationResponse = z.object({
    items: z.array(zNotificationResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * ResendDocumentResponse
 *
 * Response model for resend document action
 */
export const zResendDocumentResponse = z.object({
    resent: z.boolean(),
    envelope_id: z.string().nullable(),
    pending_signers: z.array(z.record(z.string(), z.unknown())).nullable(),
    resent_at: z.iso.datetime().nullable()
});

/**
 * ResolveDuplicateRequest
 *
 * Request to resolve a duplicate — keep one, discard the other.
 */
export const zResolveDuplicateRequest = z.object({
    keep_uuid: z.uuid(),
    discard_uuid: z.uuid()
});

/**
 * ReviewAction
 *
 * Actions an assessor can take when reviewing a classification.
 *
 * Legacy mirror of ``ClassificationDisposition`` (#313): VALIDATED/REJECTED
 * duplicate the disposition; CORRECTED is a field-edit audit mark, not a
 * disposition. Kept dual-written until the contraction phase drops it.
 */
export const zReviewAction = z.enum([
    'validated',
    'corrected',
    'rejected'
]);

/**
 * ReviewClassificationRequest
 *
 * Request to review a classification (validate or reject).
 */
export const zReviewClassificationRequest = z.object({
    action: zReviewAction
});

/**
 * ReviewFlagResolution
 *
 * How an open review flag was closed. UPPERCASE — stored verbatim in
 * the ``review_flag_resolution`` PG enum.
 */
export const zReviewFlagResolution = z.enum([
    'ACCEPTED',
    'RESOLVED',
    'SUPERSEDED'
]);

/**
 * ReviewFlagSeverity
 *
 * Declarative severity per code — derived from the catalogue, never
 * stored. UPPERCASE to match the DB enum convention of the repo.
 */
export const zReviewFlagSeverity = z.enum(['BLOCKING', 'ADVISORY']);

/**
 * ReviewFlagResponse
 *
 * A review finding with its lifecycle (#313). `severity` and `is_open`
 * are derived server-side so the frontend renders the open/blocking state
 * without re-deriving the catalogue.
 */
export const zReviewFlagResponse = z.object({
    code: z.string(),
    params: z.record(z.string(), z.unknown()),
    severity: zReviewFlagSeverity,
    is_open: z.boolean(),
    raised_at: z.iso.datetime(),
    resolved_at: z.iso.datetime().nullable(),
    resolution: zReviewFlagResolution.nullable(),
    resolved_by_id: z.int().nullable()
});

/**
 * ReviewReason
 *
 * F4+ tagged-union review reason. `extra='allow'` so the API tolerates
 * a future param key the frontend has not yet seen — the JSONB is
 * forward-compatible across deploys.
 */
export const zReviewReason = z.object({
    code: z.string(),
    params: z.record(z.string(), z.unknown()).optional()
});

/**
 * ReviewStatus
 *
 * Human review workflow status.
 */
export const zReviewStatus = z.enum([
    'pending',
    'approved',
    'rejected'
]);

/**
 * ReviewBlock
 *
 * Human approval workflow — always present on reviewable documents.
 */
export const zReviewBlock = z.object({
    status: zReviewStatus,
    reviewed_by_uuid: z.uuid().nullish(),
    reviewed_at: z.iso.datetime().nullish(),
    notes: z.string().nullish()
});

/**
 * ReviewUpdateRequest
 *
 * Request body for PATCH /{entity}/{id}/documents/{doc_id}/review.
 */
export const zReviewUpdateRequest = z.object({
    status: z.enum(['approved', 'rejected']),
    notes: z.string().nullish()
});

/**
 * RuleIdCount
 *
 * One entry in the rule-ids dropdown options. Used by the async filter
 * in the review queue to render dropdown items with usage frequency.
 */
export const zRuleIdCount = z.object({
    rule_id: z.string(),
    count: z.int()
});

/**
 * RuleIdsResponse
 *
 * Response for the `/rule-ids` endpoint — distinct rule_ids seen in
 * a beneficiary's classifications, sorted by count desc (most-used first).
 */
export const zRuleIdsResponse = z.object({
    rule_ids: z.array(zRuleIdCount)
});

/**
 * SendCommunicationContractRequest
 *
 * Request model for sending communication contract (AEAT communication).
 *
 * Attachments (signed financing contract, INAEM/ICAA certificate) are no
 * longer opt-in: the chosen template's ``pdfAttachmentBlock`` nodes
 * declare exactly which annexes the final document must contain. Pick
 * the template that matches the flow (with or without annexes).
 */
export const zSendCommunicationContractRequest = z.object({
    template_uuid: z.uuid()
});

/**
 * SendFinancingContractRequest
 *
 * Request model for sending financing contract
 */
export const zSendFinancingContractRequest = z.object({
    template_uuid: z.uuid(),
    payment_deadline: z.iso.datetime().nullish()
});

/**
 * SendReservationContractRequest
 *
 * Request model for sending reservation contract
 */
export const zSendReservationContractRequest = z.object({
    template_uuid: z.uuid(),
    payment_deadline: z.iso.datetime().nullish()
});

/**
 * SetDefaultOrganizationRequest
 *
 * Request model for setting the default organization
 */
export const zSetDefaultOrganizationRequest = z.object({
    organization_uuid: z.uuid()
});

/**
 * SignUploadBatchRequest
 *
 * Request to sign upload URLs for a batch of a deferred session's files.
 */
export const zSignUploadBatchRequest = z.object({
    classification_uuids: z.array(z.uuid()).min(1).max(500)
});

/**
 * SignatureBlockPreviewResponse
 */
export const zSignatureBlockPreviewResponse = z.object({
    kind: z.literal('signature'),
    heading_by_identity: z.record(z.string(), z.string()),
    mock_name: z.string(),
    mock_title: z.string(),
    mock_date_label: z.string()
});

/**
 * SignatureProgress
 *
 * Signing progress metrics.
 */
export const zSignatureProgress = z.object({
    total: z.int(),
    signed: z.int(),
    pending: z.int(),
    percentage: z.number()
});

/**
 * SignatureRegime
 *
 * Signing regime of a party's legal representation.
 *
 * SOLE — one representative signs alone.
 * JOINT — joint administrators ("administradores mancomunados"): every
 * representative flagged as signer must sign the contract.
 */
export const zSignatureRegime = z.enum(['sole', 'joint']);

/**
 * SignatureStatus
 *
 * Digital signature workflow status.
 *
 * Providers handle expiry differently: some auto-void expired envelopes
 * (arriving here as VOIDED), others fire a dedicated expired event.
 */
export const zSignatureStatus = z.enum([
    'pending',
    'sent',
    'signed',
    'declined',
    'voided',
    'failed'
]);

/**
 * RetryContractDispatchResponse
 *
 * Response model for retrying a failed contract dispatch.
 *
 * Same shape as the send-contract responses — the retry path runs the
 * same background dispatch and reports the same status fields back.
 */
export const zRetryContractDispatchResponse = z.object({
    contract_dispatched: z.boolean(),
    document_uuid: z.string(),
    signature_status: zSignatureStatus,
    dispatched_at: z.iso.datetime()
});

/**
 * SendCommunicationContractResponse
 *
 * Response model for send communication contract action.
 *
 * See :class:`SendReservationContractResponse` for the async dispatch
 * semantics — same shape, ``202 Accepted`` + background DocuSign call.
 */
export const zSendCommunicationContractResponse = z.object({
    contract_dispatched: z.boolean(),
    document_created: z.boolean(),
    document_uuid: z.string(),
    signature_status: zSignatureStatus,
    docusign_envelope_id: z.string().nullish(),
    dispatched_at: z.iso.datetime()
});

/**
 * SendFinancingContractResponse
 *
 * Response model for send financing contract action.
 *
 * See :class:`SendReservationContractResponse` for the async dispatch
 * semantics — same shape, ``202 Accepted`` + background DocuSign call.
 */
export const zSendFinancingContractResponse = z.object({
    contract_dispatched: z.boolean(),
    document_created: z.boolean(),
    document_uuid: z.string(),
    signature_status: zSignatureStatus,
    docusign_envelope_id: z.string().nullish(),
    dispatched_at: z.iso.datetime()
});

/**
 * SendReservationContractResponse
 *
 * Response model for send reservation contract action.
 *
 * The request returns ``202 Accepted`` as soon as the document and the
 * signature row are persisted. The DocuSign envelope is created in a
 * background task, so ``docusign_envelope_id`` is ``None`` at response
 * time — clients should poll the contract list / detail endpoint to
 * observe the signature transition ``pending → sent`` (typically a few
 * seconds).
 */
export const zSendReservationContractResponse = z.object({
    contract_dispatched: z.boolean(),
    document_created: z.boolean(),
    document_uuid: z.string(),
    signature_status: zSignatureStatus,
    docusign_envelope_id: z.string().nullish(),
    dispatched_at: z.iso.datetime()
});

/**
 * SignedUpload
 *
 * A freshly signed upload URL for one file of a deferred session.
 */
export const zSignedUpload = z.object({
    classification_uuid: z.uuid(),
    upload_url: z.string(),
    content_type: z.string()
});

/**
 * SignUploadBatchResponse
 *
 * Response with signed URLs for a batch of a deferred session.
 */
export const zSignUploadBatchResponse = z.object({
    session_id: z.uuid(),
    expires_at: z.iso.datetime(),
    uploads: z.array(zSignedUpload)
});

/**
 * SignerBlock
 *
 * Individual signer within a signature workflow.
 */
export const zSignerBlock = z.object({
    email: z.string(),
    name: z.string(),
    type: z.string(),
    status: z.string(),
    routing_order: z.int(),
    client_user_id: z.string().nullish(),
    opened_at: z.iso.datetime().nullish(),
    signed_at: z.iso.datetime().nullish()
});

/**
 * ContractSummary
 *
 * Summary of a contract document for list views.
 */
export const zContractSummary = z.object({
    document_uuid: z.uuid(),
    investment_uuid: z.uuid().nullable(),
    contract_type: z.enum([
        'reservation',
        'financing',
        'communication'
    ]),
    status: zContractDisplayStatus,
    envelope_id: z.string().nullable(),
    created_at: z.iso.datetime(),
    sent_at: z.iso.datetime().nullable(),
    completed_at: z.iso.datetime().nullable(),
    signers: z.array(zSignerBlock),
    total_signers: z.int(),
    signed_count: z.int(),
    investor_name: z.string().nullable(),
    beneficiary_name: z.string().nullable(),
    investment_amount: z.string().nullable()
});

/**
 * PaginatedResponse[ContractSummary]
 */
export const zPaginatedResponseContractSummary = z.object({
    items: z.array(zContractSummary),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * SignatureBlock
 *
 * Digital signature workflow — present on contracts/signable documents.
 */
export const zSignatureBlock = z.object({
    status: zSignatureStatus,
    envelope_id: z.string().nullable(),
    signers: z.array(zSignerBlock),
    sent_at: z.iso.datetime().nullable(),
    completed_at: z.iso.datetime().nullable(),
    last_error: z.string().nullable(),
    last_failed_at: z.iso.datetime().nullable()
});

/**
 * StateAggregationResponse
 *
 * Response model for investment aggregation by state.
 */
export const zStateAggregationResponse = z.object({
    state: zInvestmentState,
    count: z.int(),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * EntityAggregationResponse
 *
 * Response model for investment aggregation by entity.
 */
export const zEntityAggregationResponse = z.object({
    entity_uuid: z.uuid(),
    entity_name: z.string(),
    count: z.int(),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    by_state: z.array(zStateAggregationResponse)
});

/**
 * InvestmentAggregationResponse
 *
 * Response model for investment aggregation metrics.
 */
export const zInvestmentAggregationResponse = z.object({
    total_count: z.int(),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    by_state: z.array(zStateAggregationResponse),
    by_beneficiary: z.array(zEntityAggregationResponse).nullish(),
    by_investor: z.array(zEntityAggregationResponse).nullish(),
    filters_applied: z.record(z.string(), z.unknown())
});

/**
 * SupplierBreakdownResponse
 */
export const zSupplierBreakdownResponse = z.object({
    name: z.string(),
    tax_id: z.string().nullable(),
    invoice_count: z.int(),
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * SuppliersAnalyticsResponse
 */
export const zSuppliersAnalyticsResponse = z.object({
    suppliers: z.array(zSupplierBreakdownResponse),
    total_suppliers: z.int(),
    top3_concentration: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * TableBlockPreviewResponse
 */
export const zTableBlockPreviewResponse = z.object({
    kind: z.literal('table'),
    columns: z.array(z.string()),
    sample_rows: z.array(z.array(z.string()))
});

/**
 * BlockResponse
 *
 * Response schema for a single compound block.
 */
export const zBlockResponse = z.object({
    key: z.string(),
    description: z.string(),
    preview: z.discriminatedUnion('kind', [
        zListBlockPreviewResponse,
        zTableBlockPreviewResponse,
        zSignatureBlockPreviewResponse,
        zAttachmentBlockPreviewResponse,
        zDescriptionBlockPreviewResponse,
        zImageBlockPreviewResponse
    ]),
    variants: z.array(zBlockVariantResponse)
});

/**
 * BlockGroupResponse
 *
 * Response schema for a group of blocks.
 */
export const zBlockGroupResponse = z.object({
    group: z.string(),
    items: z.array(zBlockResponse)
});

/**
 * CatalogResponse
 *
 * Response schema for the full catalog.
 *
 * Split into two top-level collections:
 * - fields: scalar values substituted inline.
 * - blocks: compound structures with their own editor nodes.
 */
export const zCatalogResponse = z.object({
    fields: z.array(zFieldGroupResponse),
    blocks: z.array(zBlockGroupResponse),
    total_fields: z.int(),
    total_blocks: z.int()
});

/**
 * TaxBreakdownAnalyticsResponse
 */
export const zTaxBreakdownAnalyticsResponse = z.object({
    total_iva: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    total_irpf: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    iva_by_rate: z.array(zIvaRateBreakdownResponse)
});

/**
 * TaxIncentiveType
 */
export const zTaxIncentiveType = z.enum(['film_audiovisual', 'performing_arts_music']);

/**
 * BeneficiarySummary
 *
 * Summary model for beneficiary data in other contexts (e.g., project responses)
 */
export const zBeneficiarySummary = z.object({
    uuid: z.uuid(),
    name: z.string(),
    tax_id: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    incentive_type: zTaxIncentiveType.nullable()
});

/**
 * ConsolidationPreviewResponse
 *
 * GET /beneficiaries/{uuid}/fiscal-quotas/{fiscal_quota_uuid}/consolidation-preview.
 */
export const zConsolidationPreviewResponse = z.object({
    fiscal_quota_uuid: z.uuid(),
    incentive_type: zTaxIncentiveType.nullable(),
    buckets: z.array(zConsolidationBucketPreviewResponse),
    consolidated_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    consolidated_eligible_cost: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    consolidated_deduction_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    consolidated_generated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    pending_review_count: z.int(),
    pending_review_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    pending_review_potential_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    last_consolidated_at: z.iso.datetime().nullable(),
    last_consolidated_by_id: z.int().nullable(),
    new_since_last_sync: z.int().nullable()
});

/**
 * InvestmentResponse
 *
 * Response model for investment summary data
 */
export const zInvestmentResponse = z.object({
    uuid: z.uuid(),
    amount: z.string(),
    return_percentage: z.string(),
    currency: z.string(),
    state: zInvestmentState,
    archived: z.boolean(),
    reservation_expires_at: z.iso.datetime().nullish(),
    notes: z.string().nullish(),
    created_at: z.iso.datetime().nullish(),
    updated_at: z.iso.datetime().nullish(),
    investor: zInvestorSummary,
    beneficiary: zBeneficiarySummary,
    quota: zFiscalQuotaSummary
});

/**
 * ProjectCreateRequest
 *
 * Request model for creating a project
 */
export const zProjectCreateRequest = z.object({
    title: z.string(),
    tax_incentive_type: zTaxIncentiveType,
    subtype: zProjectSubtype,
    fiscal_quota_uuid: z.uuid(),
    description: z.string().nullish(),
    fundraising_date: z.iso.date().nullish(),
    production_start_date: z.iso.date().nullish(),
    production_end_date: z.iso.date().nullish(),
    fiscal_year: z.int().nullish(),
    city: z.string().nullish()
});

/**
 * ProjectDetailResponse
 *
 * Extended response model for project details including investors information.
 * Inherits all fields from ProjectResponse and adds investor data plus
 * financial enrichment sourced from the linked fiscal quota.
 */
export const zProjectDetailResponse = z.object({
    uuid: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    tax_incentive_type: zTaxIncentiveType,
    subtype: zProjectSubtype,
    fundraising_date: z.iso.date().nullable(),
    production_start_date: z.iso.date().nullable(),
    production_end_date: z.iso.date().nullable(),
    fiscal_year: z.int().nullable(),
    city: z.string().nullable(),
    status: zEntityStatus,
    is_ready_for_investment: z.boolean(),
    has_poster: z.boolean(),
    beneficiary: zBeneficiarySummary,
    fiscal_quota_uuid: z.uuid().nullish(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    investors: z.array(zInvestorProjectSummary).optional().default([]),
    expected_profitability_percentage: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish(),
    max_investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish()
});

/**
 * ProjectResponse
 *
 * Response model for project data
 */
export const zProjectResponse = z.object({
    uuid: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    tax_incentive_type: zTaxIncentiveType,
    subtype: zProjectSubtype,
    fundraising_date: z.iso.date().nullable(),
    production_start_date: z.iso.date().nullable(),
    production_end_date: z.iso.date().nullable(),
    fiscal_year: z.int().nullable(),
    city: z.string().nullable(),
    status: zEntityStatus,
    is_ready_for_investment: z.boolean(),
    has_poster: z.boolean(),
    beneficiary: zBeneficiarySummary,
    fiscal_quota_uuid: z.uuid().nullish(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * FilteredPaginatedResponse[ProjectResponse]
 */
export const zFilteredPaginatedResponseProjectResponse = z.object({
    items: z.array(zProjectResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int(),
    total_unfiltered_count: z.int()
});

/**
 * ListResponse[ProjectResponse]
 */
export const zListResponseProjectResponse = z.object({
    items: z.array(zProjectResponse),
    total_count: z.int()
});

/**
 * ProjectBulkCreateResponse
 *
 * Returns every created project so the client can refresh its caches
 * with the new entities without a follow-up GET.
 */
export const zProjectBulkCreateResponse = z.object({
    projects: z.array(zProjectResponse)
});

/**
 * ProjectUpdateRequest
 *
 * Request model for updating a project
 */
export const zProjectUpdateRequest = z.object({
    title: z.string().nullish(),
    description: z.string().nullish(),
    tax_incentive_type: zTaxIncentiveType.nullish(),
    subtype: zProjectSubtype.nullish(),
    status: zEntityStatus.nullish(),
    fundraising_date: z.iso.date().nullish(),
    production_start_date: z.iso.date().nullish(),
    production_end_date: z.iso.date().nullish(),
    fiscal_year: z.int().nullish(),
    city: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish()
});

/**
 * TemplateCategory
 *
 * Template categories for organizing templates in the UI
 */
export const zTemplateCategory = z.enum([
    'legal',
    'financial',
    'administrative',
    'commercial',
    'technical'
]);

/**
 * TemplateCompatibilityResponse
 *
 * Computed compatibility of a template with a document-generating act.
 *
 * ``unknown_keys`` — referenced keys missing from the catalog entirely
 * (stale template after a catalog change). ``unresolvable_keys`` —
 * catalogued keys the act's context cannot produce. ``missing_annexes``
 * — declared annex variants whose backing document does not exist yet
 * (only evaluated when the caller asks for annex checks); each has a
 * localizable code in ``missing_annex_codes``.
 */
export const zTemplateCompatibilityResponse = z.object({
    compatible: z.boolean(),
    unknown_keys: z.array(z.string()),
    unresolvable_keys: z.array(z.string()),
    required_annexes: z.array(z.string()),
    unsupported_annexes: z.array(z.string()),
    missing_annexes: z.array(z.string()),
    missing_annex_codes: z.array(z.string())
});

/**
 * TemplateImagePreviewRequest
 *
 * Payload for resolving an ImageBlock src back to a renderable URL.
 */
export const zTemplateImagePreviewRequest = z.object({
    src: z.string()
});

/**
 * TemplateImagePreviewResponse
 *
 * Signed URL the editor can drop into an <img> tag.
 */
export const zTemplateImagePreviewResponse = z.object({
    signed_url: z.string()
});

/**
 * TemplateImageUploadRequest
 *
 * Upload payload for an ImageBlock asset (data URI base64).
 *
 * Mirrors ``OrganizationUpdateRequest.logo`` — the frontend posts a
 * ``data:image/<ext>;base64,...`` string that the storage service parses,
 * validates and persists to GCS.
 */
export const zTemplateImageUploadRequest = z.object({
    data_uri: z.string()
});

/**
 * TemplateImageUploadResponse
 *
 * Response returned after a successful template image upload.
 */
export const zTemplateImageUploadResponse = z.object({
    src: z.string(),
    signed_url: z.string()
});

/**
 * TemplateIndustry
 *
 * Template industry categories for filtering
 */
export const zTemplateIndustry = z.enum([
    'music',
    'audiovisual',
    'general'
]);

/**
 * TemplateLanguage
 *
 * Template language support
 */
export const zTemplateLanguage = z.enum([
    'es',
    'en',
    'fr',
    'de'
]);

/**
 * TemplateType
 *
 * Template type enum for templates domain
 */
export const zTemplateType = z.enum([
    'contract',
    'nda',
    'proposal',
    'invoice',
    'agreement',
    'report',
    'certificate',
    'letter',
    'reservation_agreement'
]);

/**
 * TemplateCreateRequest
 *
 * Request schema for creating a new template.
 */
export const zTemplateCreateRequest = z.object({
    name: z.string(),
    description: z.string().nullish(),
    type: zTemplateType,
    category: zTemplateCategory,
    language: zTemplateLanguage.optional().default('es'),
    industry: zTemplateIndustry.optional().default('general'),
    settings: z.record(z.string(), z.unknown()).optional(),
    content: z.array(z.record(z.string(), z.unknown())).nullish()
});

/**
 * TemplateDetailResponse
 *
 * Full response with document content.
 */
export const zTemplateDetailResponse = z.object({
    uuid: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    type: zTemplateType,
    category: zTemplateCategory,
    language: zTemplateLanguage,
    industry: zTemplateIndustry,
    is_system_template: z.boolean(),
    is_active: z.boolean(),
    version: z.string(),
    updated_at: z.iso.datetime().nullable(),
    settings: z.record(z.string(), z.unknown()),
    content: z.array(z.record(z.string(), z.unknown())).nullable()
});

/**
 * TemplateSummaryResponse
 *
 * Summary response for template list items.
 */
export const zTemplateSummaryResponse = z.object({
    uuid: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    type: zTemplateType,
    category: zTemplateCategory,
    language: zTemplateLanguage,
    industry: zTemplateIndustry,
    is_system_template: z.boolean(),
    is_active: z.boolean(),
    version: z.string(),
    updated_at: z.iso.datetime().nullable()
});

/**
 * PaginatedResponse[TemplateSummaryResponse]
 */
export const zPaginatedResponseTemplateSummaryResponse = z.object({
    items: z.array(zTemplateSummaryResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * TemplateUpdateRequest
 *
 * Request schema for updating a template (partial).
 */
export const zTemplateUpdateRequest = z.object({
    name: z.string().nullish(),
    description: z.string().nullish(),
    type: zTemplateType.nullish(),
    category: zTemplateCategory.nullish(),
    language: zTemplateLanguage.nullish(),
    industry: zTemplateIndustry.nullish(),
    settings: z.record(z.string(), z.unknown()).nullish(),
    content: z.array(z.record(z.string(), z.unknown())).nullish()
});

/**
 * TokenUsageResponse
 *
 * Token usage from LLM calls.
 */
export const zTokenUsageResponse = z.object({
    tokens_input: z.int(),
    tokens_output: z.int(),
    tokens_total: z.int()
});

/**
 * ToolType
 *
 * Available agent tool types.
 */
export const zToolType = z.enum(['sql_query', 'knowledge_base']);

/**
 * UploadItem
 *
 * A single file's upload details within a session.
 *
 * `upload_url` is None for deferred sessions — the client requests it
 * later through the signed-urls endpoint.
 */
export const zUploadItem = z.object({
    classification_uuid: z.uuid(),
    filename: z.string(),
    upload_url: z.string().nullable(),
    gcs_path: z.string(),
    content_type: z.string()
});

/**
 * CreateUploadSessionResponse
 *
 * Response after creating an upload session.
 */
export const zCreateUploadSessionResponse = z.object({
    session_id: z.uuid(),
    expires_at: z.iso.datetime(),
    uploads: z.array(zUploadItem)
});

/**
 * UploadSessionStatus
 *
 * Status lifecycle for an upload session.
 */
export const zUploadSessionStatus = z.enum([
    'open',
    'processing',
    'completed',
    'partial',
    'expired'
]);

/**
 * UploadSessionStatusResponse
 *
 * Response for checking session progress.
 */
export const zUploadSessionStatusResponse = z.object({
    session_id: z.uuid(),
    status: zUploadSessionStatus,
    expected_count: z.int(),
    received_count: z.int(),
    completed_count: z.int(),
    failed_count: z.int(),
    needs_review_count: z.int(),
    created_at: z.iso.datetime(),
    expires_at: z.iso.datetime(),
    completed_at: z.iso.datetime().nullable()
});

/**
 * UsageInfoResponse
 *
 * Daily usage info for the current user/module.
 */
export const zUsageInfoResponse = z.object({
    tokens_used_today: z.int(),
    tokens_limit_today: z.int(),
    pct_used: z.number(),
    warning: z.boolean(),
    queries_today: z.int()
});

/**
 * AgentQueryResponse
 *
 * Response DTO for agent query.
 */
export const zAgentQueryResponse = z.object({
    query_id: z.uuid(),
    answer: z.string(),
    status: zAgentResponseStatus,
    session_id: z.string().nullish(),
    tool_used: zToolType.nullish(),
    generated_sql: zGeneratedSqlResponse.nullish(),
    result: zQueryResultResponse.nullish(),
    sources: z.array(z.string()).nullish(),
    actions: z.array(zAgentActionResponse).optional(),
    error_message: z.string().nullish(),
    processing_time_ms: z.number(),
    token_usage: zTokenUsageResponse.nullish(),
    usage: zUsageInfoResponse.nullish()
});

/**
 * UsageMetricResponse
 *
 * One measured quantity in a domain usage report.
 */
export const zUsageMetricResponse = z.object({
    key: z.string(),
    label: z.string(),
    value: z.int(),
    unit: zQuotaUnit,
    cost_usd: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish()
});

/**
 * DomainUsageReportResponse
 *
 * A domain's usage over a period for the admin panel.
 */
export const zDomainUsageReportResponse = z.object({
    domain: z.string(),
    metrics: z.array(zUsageMetricResponse),
    status_breakdown: z.record(z.string(), z.int()).nullish(),
    total_cost_usd: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullish()
});

/**
 * UserCreateRequest
 *
 * Request model for creating a new user
 */
export const zUserCreateRequest = z.object({
    email: z.email(),
    password: z.string().min(8),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    language: z.string().optional().default('en'),
    profile_picture: z.string().nullish(),
    is_platform_admin: z.boolean().optional().default(false)
});

/**
 * OrganizationSetupData
 *
 * Completion data for CREATE_ORG invitations - creates user and completes organization
 */
export const zOrganizationSetupData = z.object({
    type: z.literal('organization_setup'),
    user: zUserCreateRequest,
    org: zOrganizationUpdateRequest
});

/**
 * UserInvitationResponse
 *
 * Response model for user invitations with organization details
 */
export const zUserInvitationResponse = z.object({
    uuid: z.uuid(),
    organization: zOrganizationSummary,
    role: zOrganizationRole,
    status: zInvitationStatus,
    invitation_type: zInvitationType,
    expires_at: z.iso.datetime(),
    accepted_at: z.iso.datetime().nullish(),
    created_at: z.iso.datetime()
});

/**
 * ListResponse[UserInvitationResponse]
 */
export const zListResponseUserInvitationResponse = z.object({
    items: z.array(zUserInvitationResponse),
    total_count: z.int()
});

/**
 * UserPreferencesResponse
 *
 * Response model for user preferences
 */
export const zUserPreferencesResponse = z.object({
    language: z.string(),
    timezone: z.string(),
    prefers_dark_mode: z.boolean(),
    prefers_compact_view: z.boolean(),
    notifications_enabled: z.boolean(),
    notify_via_email: z.boolean(),
    notify_via_app: z.boolean()
});

/**
 * UserPreferencesUpdate
 *
 * Schema for user preferences update
 */
export const zUserPreferencesUpdate = z.object({
    language: z.string().nullish(),
    timezone: z.string().nullish(),
    prefers_dark_mode: z.boolean().nullish(),
    prefers_compact_view: z.boolean().nullish(),
    notifications_enabled: z.boolean().nullish(),
    notify_via_email: z.boolean().nullish(),
    notify_via_app: z.boolean().nullish()
});

/**
 * UserRegistrationData
 *
 * Completion data for JOIN_AS_NEW_USER invitations - creates user and membership.
 * For role=INVESTOR, the investor link is created automatically using the
 * investor_uuid embedded in the signed invitation token.
 */
export const zUserRegistrationData = z.object({
    type: z.literal('user_registration'),
    user: zUserCreateRequest
});

/**
 * InvitationCompletionRequest
 *
 * Request schema for completing an invitation
 */
export const zInvitationCompletionRequest = z.object({
    data: z.discriminatedUnion('type', [
        zOrganizationSetupData,
        zUserRegistrationData,
        zMembershipJoinData,
        zInvestorHubRegistrationData
    ])
});

/**
 * UserResponse
 *
 * Response model for user data
 */
export const zUserResponse = z.object({
    uuid: z.uuid(),
    email: z.string(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    profile_picture_signed_url: z.string().nullish(),
    is_active: z.boolean(),
    is_verified: z.boolean(),
    is_platform_admin: z.boolean(),
    created_at: z.iso.datetime().nullish(),
    updated_at: z.iso.datetime().nullish(),
    last_login_at: z.iso.datetime().nullish(),
    full_name: z.string(),
    preferences: zUserPreferencesResponse.nullish()
});

/**
 * InvitationCompletionResponse
 *
 * Response schema for invitation completion
 */
export const zInvitationCompletionResponse = z.object({
    auth: zAuthTokenResponse,
    user: zUserResponse,
    organization: zOrganizationSummary
});

/**
 * UserSummary
 *
 * Response model for user summary data
 */
export const zUserSummary = z.object({
    uuid: z.uuid(),
    email: z.string(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    full_name: z.string(),
    profile_picture_signed_url: z.string().nullish(),
    last_login_at: z.iso.datetime().nullish()
});

/**
 * InvestmentEventResponse
 *
 * Response model for investment event data
 */
export const zInvestmentEventResponse = z.object({
    uuid: z.uuid(),
    event_type: zInvestmentEventType,
    occurred_at: z.iso.datetime(),
    user: zUserSummary.nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    created_at: z.iso.datetime()
});

/**
 * MembershipResponse
 *
 * Response model for membership data
 */
export const zMembershipResponse = z.object({
    uuid: z.uuid(),
    user: zUserSummary,
    organization: zOrganizationSummary,
    role: zOrganizationRole,
    is_default: z.boolean(),
    is_active: z.boolean(),
    joined_at: z.iso.datetime()
});

/**
 * ListResponse[MembershipResponse]
 */
export const zListResponseMembershipResponse = z.object({
    items: z.array(zMembershipResponse),
    total_count: z.int()
});

/**
 * UserUpdate
 *
 * Schema for user profile update
 */
export const zUserUpdate = z.object({
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    profile_picture: z.string().nullish(),
    preferences: zUserPreferencesUpdate.nullish()
});

/**
 * VoidContractRequest
 *
 * Request model for voiding contract
 */
export const zVoidContractRequest = z.object({
    document_uuid: z.string()
});

/**
 * VoidContractResponse
 *
 * Response model for void contract action
 */
export const zVoidContractResponse = z.object({
    document_voided: z.boolean(),
    signing_cancelled: z.boolean(),
    voided_at: z.iso.datetime()
});

/**
 * WorkerBreakdownResponse
 */
export const zWorkerBreakdownResponse = z.object({
    tax_id: z.string(),
    name: z.string(),
    position: z.string().nullable(),
    total_earnings: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    months: z.int()
});

/**
 * WorkforceAnalyticsResponse
 */
export const zWorkforceAnalyticsResponse = z.object({
    total_payroll: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/),
    worker_count: z.int(),
    workers: z.array(zWorkerBreakdownResponse)
});

/**
 * CulturalActivity
 */
export const zAppDomainBeneficiariesEnumsCulturalActivity = z.enum([
    'film',
    'documentary',
    'series',
    'theatre',
    'dance',
    'music_festival',
    'performing_arts',
    'audiovisual_production'
]);

/**
 * BeneficiaryCreateRequest
 *
 * Request model for creating a beneficiary
 */
export const zBeneficiaryCreateRequest = z.object({
    name: z.string(),
    tax_id: z.string(),
    company_type: zCompanyType,
    main_activities: z.array(zAppDomainBeneficiariesEnumsCulturalActivity),
    constitution_date: z.iso.date().nullish(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postal_code: z.string().nullish(),
    country: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    website: z.string().nullish(),
    notes: z.string().nullish(),
    business_purpose: z.string().max(2000).nullish(),
    cnae_code: z.string().max(10).nullish(),
    last_year_revenue: z.union([
        z.number().gte(0),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
    ]).nullish(),
    employee_count: z.int().gte(0).lte(1000000).nullish(),
    incentive_type: zTaxIncentiveType.nullish()
});

/**
 * BeneficiaryResponse
 *
 * Response model for beneficiary data
 */
export const zBeneficiaryResponse = z.object({
    uuid: z.uuid(),
    name: z.string(),
    tax_id: z.string(),
    company_type: zCompanyType,
    main_activities: z.array(zAppDomainBeneficiariesEnumsCulturalActivity),
    status: zEntityStatus,
    constitution_date: z.iso.date().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    notes: z.string().nullable(),
    business_purpose: z.string().nullable(),
    cnae_code: z.string().nullable(),
    last_year_revenue: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable(),
    employee_count: z.int().nullable(),
    incentive_type: zTaxIncentiveType.nullable(),
    signature_regime: zSignatureRegime.default('sole'),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * BeneficiaryUpdateRequest
 *
 * Request model for updating a beneficiary
 */
export const zBeneficiaryUpdateRequest = z.object({
    name: z.string().nullish(),
    tax_id: z.string().nullish(),
    company_type: zCompanyType.nullish(),
    main_activities: z.array(zAppDomainBeneficiariesEnumsCulturalActivity).nullish(),
    status: zEntityStatus.nullish(),
    constitution_date: z.iso.date().nullish(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postal_code: z.string().nullish(),
    country: z.string().nullish(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    website: z.string().nullish(),
    notes: z.string().nullish(),
    business_purpose: z.string().max(2000).nullish(),
    cnae_code: z.string().max(10).nullish(),
    last_year_revenue: z.union([
        z.number().gte(0),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
    ]).nullish(),
    employee_count: z.int().gte(0).lte(1000000).nullish(),
    incentive_type: zTaxIncentiveType.nullish(),
    signature_regime: zSignatureRegime.nullish()
});

/**
 * DocumentType
 *
 * Types of documents the classifier can process.
 */
export const zAppDomainClassificationsEnumsDocumentType = z.enum([
    'factura',
    'nomina',
    'presupuesto',
    'proforma',
    'albaran',
    'contrato',
    'comprobante_pago',
    'certificado',
    'rider',
    'desconocido'
]);

/**
 * ClassificationResponse
 *
 * Response for a single document classification.
 */
export const zClassificationResponse = z.object({
    uuid: z.uuid(),
    status: zClassificationStatus,
    document_type: zAppDomainClassificationsEnumsDocumentType.nullable(),
    source_filename: z.string(),
    parent_document_uuid: z.uuid().nullable(),
    parent_document_name: z.string().nullable(),
    extracted_fields: z.union([
        zInvoiceFields,
        zPayrollFields,
        zContractFields,
        zPaymentFields,
        zCertificateFields
    ]).nullable(),
    classification_result: zClassificationResultData.nullable(),
    expense_type: z.string().nullable(),
    expense_subtype: z.string().nullable(),
    fiscal_category: z.string().nullable(),
    eligible: z.boolean().nullable(),
    confidence: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    deductible_base_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    currency: z.string(),
    document_date: z.iso.date().nullable(),
    requires_review: z.boolean(),
    review_reasons: z.array(zReviewReason),
    review_flags: z.array(zReviewFlagResponse),
    disposition: zClassificationDisposition,
    review_action: zReviewAction.nullable(),
    reviewed_by_id: z.int().nullable(),
    reviewed_at: z.iso.datetime().nullable(),
    review_notes: z.string().nullable(),
    beneficiary_comment: z.string().nullable(),
    corrected_fields: z.record(z.string(), z.unknown()).nullable(),
    field_locks: z.array(zFieldLockResponse),
    processing_time_ms: z.number().nullable(),
    tokens_used: z.int(),
    created_at: z.iso.datetime(),
    processed_at: z.iso.datetime().nullable(),
    uploaded_by_user: zUserSummary.nullable(),
    reviewed_by_user: zUserSummary.nullable(),
    eligibility_decision: zEligibilityDecisionResponse.nullable()
});

/**
 * DocumentTypeCountResponse
 */
export const zDocumentTypeCountResponse = z.object({
    document_type: zAppDomainClassificationsEnumsDocumentType,
    count: z.int(),
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
});

/**
 * DocumentInventoryResponse
 */
export const zDocumentInventoryResponse = z.object({
    total_count: z.int(),
    by_type: z.array(zDocumentTypeCountResponse)
});

/**
 * ClassificationSummaryResponse
 */
export const zClassificationSummaryResponse = z.object({
    financials: zFinancialMetricsResponse,
    document_inventory: zDocumentInventoryResponse,
    conciliation: zConciliationMetricsResponse,
    monthly: z.array(zMonthlyBreakdownResponse),
    categories: z.array(zCategoryBreakdownResponse),
    pipeline_status: zPipelineStatusBreakdownResponse
});

/**
 * FilteredPaginatedResponse[ClassificationResponse]
 */
export const zFilteredPaginatedResponseClassificationResponse = z.object({
    items: z.array(zClassificationResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int(),
    total_unfiltered_count: z.int()
});

/**
 * ResolveDuplicateResponse
 *
 * Response from resolve-duplicate endpoint.
 */
export const zResolveDuplicateResponse = z.object({
    kept: zClassificationResponse,
    discarded: zClassificationResponse
});

/**
 * UpdateClassificationRequest
 *
 * Request to update classification data (extracted fields, categorization, qualification).
 */
export const zUpdateClassificationRequest = z.object({
    document_type: zAppDomainClassificationsEnumsDocumentType.nullish(),
    extracted_fields: z.record(z.string(), z.unknown()).nullish(),
    categorization: z.record(z.string(), z.unknown()).nullish(),
    qualification: z.record(z.string(), z.unknown()).nullish(),
    notes: z.string().max(2000).nullish(),
    beneficiary_comment: z.string().max(2000).nullish()
});

/**
 * DocumentType
 *
 * Unified document types enum - combines all document types across entity types.
 * Used for the unified documents table.
 */
export const zAppDomainDocumentsEnumsDocumentType = z.enum([
    'investment_proposal',
    'reservation_contract',
    'financing_contract',
    'communication_contract',
    'payment_receipt',
    'tax_submission_certificate',
    'national_id',
    'passport',
    'residence_permit',
    'tax_certificate',
    'income_statement',
    'wealth_declaration',
    'company_registration',
    'articles_of_association',
    'power_of_attorney',
    'bank_account_certificate',
    'iban_certificate',
    'kyc_form',
    'proof_of_address',
    'other',
    'tax_id_certificate',
    'census_certificate',
    'icaa_certificate',
    'inaem_certificate',
    'invoice',
    'payroll',
    'poster',
    'expense_audit'
]);

/**
 * Body_upload_beneficiary_document
 */
export const zBodyUploadBeneficiaryDocument = z.object({
    document: z.string(),
    document_type: zAppDomainDocumentsEnumsDocumentType,
    entity_type: zEntityType,
    entity_uuid: z.uuid(),
    title: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    share_with_investors: z.boolean().optional().default(false)
});

/**
 * Body_upload_investor_document
 */
export const zBodyUploadInvestorDocument = z.object({
    file: z.string(),
    doc_type: zAppDomainDocumentsEnumsDocumentType,
    title: z.string().nullish()
});

/**
 * Body_upload_organization_document
 */
export const zBodyUploadOrganizationDocument = z.object({
    document: z.string(),
    document_type: zAppDomainDocumentsEnumsDocumentType,
    title: z.string().nullish()
});

/**
 * Body_upload_project_document
 */
export const zBodyUploadProjectDocument = z.object({
    document: z.string(),
    document_type: zAppDomainDocumentsEnumsDocumentType,
    title: z.string().nullish()
});

/**
 * DocumentBlock
 *
 * The file primitive — shared across all document responses.
 */
export const zDocumentBlock = z.object({
    uuid: z.uuid(),
    doc_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus,
    name: z.string(),
    title: z.string().nullish(),
    size_bytes: z.int(),
    content_type: z.string(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_quota_year: z.int().nullish(),
    shared_with_investors: z.boolean().optional().default(false),
    uploaded_at: z.iso.datetime().nullish(),
    uploaded_by_uuid: z.uuid().nullish()
});

/**
 * ContractDetailsResponse
 *
 * Detailed contract response — composed from shared blocks + investment context.
 */
export const zContractDetailsResponse = z.object({
    document: zDocumentBlock,
    signature: zSignatureBlock,
    investment: zContractInvestmentContext,
    contract_type: z.enum([
        'reservation',
        'financing',
        'communication'
    ]),
    voided_at: z.iso.datetime().nullish(),
    signature_progress: zSignatureProgress
});

/**
 * DocumentWithReviewResponse
 *
 * Document with review — used for beneficiary, investor, org, and project documents.
 */
export const zDocumentWithReviewResponse = z.object({
    document: zDocumentBlock,
    review: zReviewBlock
});

/**
 * BankAccountDocumentGroupResponse
 *
 * Documents attached to a single bank account of a beneficiary.
 */
export const zBankAccountDocumentGroupResponse = z.object({
    bank_account_uuid: z.uuid(),
    bank_name: z.string().nullish(),
    iban_last4: z.string().nullish(),
    documents: z.array(zDocumentWithReviewResponse)
});

/**
 * FiscalQuotaDocumentGroupResponse
 *
 * Beneficiary-owned documents grouped under a single fiscal quota.
 *
 * The unscoped group (``fiscal_quota_uuid=None``) carries documents not
 * linked to any quota and is rendered as the "General" section. For
 * quota groups, the frontend builds the header label from
 * ``project_title`` (when present) and ``fiscal_year`` following the
 * same rule as ``FiscalQuotaCard``:
 *
 * - music quota (``project_title=None``): ``"Cuota {year}"``
 * - audiovisual quota: ``"{project_title} · {year}"``
 *
 * ``total`` is the count across all pages; ``documents`` only carries
 * the first page (up to ``per_group_limit`` items requested by the API).
 */
export const zFiscalQuotaDocumentGroupResponse = z.object({
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_year: z.int().nullish(),
    project_uuid: z.uuid().nullish(),
    project_title: z.string().nullish(),
    total: z.int(),
    documents: z.array(zDocumentWithReviewResponse)
});

/**
 * BeneficiaryDocumentsByFiscalQuotaResponse
 *
 * Beneficiary-owned documents grouped by fiscal quota.
 *
 * The top-level ``incentive_type`` is propagated from the beneficiary so
 * the frontend can pick the right icon + header naming format without an
 * extra round-trip. Groups are ordered: unscoped first, then quotas DESC
 * by ``fiscal_year``.
 */
export const zBeneficiaryDocumentsByFiscalQuotaResponse = z.object({
    beneficiary_uuid: z.uuid(),
    incentive_type: zTaxIncentiveType.nullish(),
    groups: z.array(zFiscalQuotaDocumentGroupResponse)
});

/**
 * InvestmentDocumentResponse
 *
 * Investment document — review always present, signature optional (only contracts).
 */
export const zInvestmentDocumentResponse = z.object({
    document: zDocumentBlock,
    review: zReviewBlock,
    signature: zSignatureBlock.nullish()
});

/**
 * InvestmentDocumentGroupResponse
 *
 * Documents of a single investment with the metadata the UI needs to render
 * a section header (reference, state, amount, beneficiary name).
 */
export const zInvestmentDocumentGroupResponse = z.object({
    investment_uuid: z.uuid(),
    investment_reference: z.string(),
    investment_state: zInvestmentState,
    amount: z.string(),
    currency: z.string(),
    beneficiary_name: z.string(),
    fiscal_year: z.int().nullish(),
    documents: z.array(zInvestmentDocumentResponse)
});

/**
 * BeneficiaryPortfolioResponse
 *
 * Aggregated view of every document relevant to a beneficiary.
 *
 * The document set is paginated at the flat level (``total_count`` /
 * ``limit`` / ``offset``); sections are rebuilt from whichever documents
 * fall on the current page.
 */
export const zBeneficiaryPortfolioResponse = z.object({
    beneficiary_uuid: z.uuid(),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int(),
    owned: z.array(zDocumentWithReviewResponse),
    from_bank_accounts: z.array(zBankAccountDocumentGroupResponse),
    from_investments: z.array(zInvestmentDocumentGroupResponse)
});

/**
 * InvestmentWithDetailsResponse
 *
 * Response model for investment with all nested details
 */
export const zInvestmentWithDetailsResponse = z.object({
    investment: zInvestmentResponse,
    events: z.array(zInvestmentEventResponse),
    documents: z.array(zInvestmentDocumentResponse)
});

/**
 * FilteredPaginatedResponse[InvestmentWithDetailsResponse]
 */
export const zFilteredPaginatedResponseInvestmentWithDetailsResponse = z.object({
    items: z.array(zInvestmentWithDetailsResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int(),
    total_unfiltered_count: z.int()
});

/**
 * InvestorPortfolioResponse
 *
 * Aggregated view of every document relevant to an investor.
 *
 * The document set is paginated at the flat level (``total_count`` /
 * ``limit`` / ``offset``); sections are rebuilt from whichever documents
 * fall on the current page.
 */
export const zInvestorPortfolioResponse = z.object({
    investor_uuid: z.uuid(),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int(),
    owned: z.array(zDocumentWithReviewResponse),
    from_investments: z.array(zInvestmentDocumentGroupResponse)
});

/**
 * PaginatedResponse[DocumentWithReviewResponse]
 */
export const zPaginatedResponseDocumentWithReviewResponse = z.object({
    items: z.array(zDocumentWithReviewResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * PaginatedResponse[InvestmentDocumentResponse]
 */
export const zPaginatedResponseInvestmentDocumentResponse = z.object({
    items: z.array(zInvestmentDocumentResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * CulturalActivity
 *
 * Cultural activity types for investor interests
 */
export const zAppDomainInvestorsEnumsCulturalActivity = z.enum([
    'film',
    'documentary',
    'series',
    'theatre',
    'dance',
    'music_festival',
    'performing_arts',
    'audiovisual_production'
]);

/**
 * CompanyCreateRequest
 *
 * Request schema for creating a company investor
 */
export const zCompanyCreateRequest = z.object({
    company_name: z.string().max(255),
    tax_id: z.string().max(32),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(160).nullish(),
    legal_rep_national_id: z.string().max(32).nullish(),
    website: z.string().max(255).nullish(),
    signature_regime: zSignatureRegime.optional().default('sole'),
    email: z.email().nullish(),
    phone: z.string().max(50).nullish(),
    address: z.string().nullish(),
    city: z.string().max(120).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    communication_language: z.string().max(2).optional().default('es'),
    notes: z.string().nullish(),
    taxable_income_base: z.string().nullish(),
    potential_investment_amount: z.string().nullish(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).optional().default([])
});

/**
 * CompanyResponse
 *
 * Response schema for company investor data
 */
export const zCompanyResponse = z.object({
    uuid: z.uuid(),
    organization_id: z.int(),
    type: z.literal('company').default('company'),
    status: zInvestorStatus,
    company_name: z.string(),
    tax_id: z.string(),
    legal_rep_first_name: z.string().nullable(),
    legal_rep_last_name: z.string().nullable(),
    legal_rep_national_id: z.string().nullable(),
    website: z.string().nullable(),
    signature_regime: zSignatureRegime.default('sole'),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    communication_language: z.string(),
    notes: z.string().nullable(),
    taxable_income_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    potential_investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).default([]),
    legal_representatives: z.array(zInvestorLegalRepresentativeResponse).default([]),
    display_name: z.string(),
    legal_representative_name: z.string().nullable(),
    full_address: z.string().nullable(),
    tax_identifier: z.string(),
    hub_status: zHubStatus.default('none'),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime().nullable()
});

/**
 * CompanyUpdateRequest
 *
 * Request schema for updating a company investor
 */
export const zCompanyUpdateRequest = z.object({
    status: zInvestorStatus.nullish(),
    company_name: z.string().max(255).nullish(),
    tax_id: z.string().max(32).nullish(),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(160).nullish(),
    legal_rep_national_id: z.string().max(32).nullish(),
    website: z.string().max(255).nullish(),
    signature_regime: zSignatureRegime.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    national_id: z.string().nullish(),
    birth_date: z.iso.date().nullish(),
    profession: z.string().nullish(),
    email: z.email().nullish(),
    phone: z.string().max(50).nullish(),
    address: z.string().nullish(),
    city: z.string().max(120).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    communication_language: z.string().max(2).nullish(),
    notes: z.string().nullish(),
    taxable_income_base: z.string().nullish(),
    potential_investment_amount: z.string().nullish(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).nullish()
});

/**
 * IndividualCreateRequest
 *
 * Request schema for creating an individual investor
 */
export const zIndividualCreateRequest = z.object({
    first_name: z.string().max(120),
    last_name: z.string().max(160),
    national_id: z.string().max(32),
    birth_date: z.iso.date().nullish(),
    profession: z.string().max(160).nullish(),
    email: z.email().nullish(),
    phone: z.string().max(50).nullish(),
    address: z.string().nullish(),
    city: z.string().max(120).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    communication_language: z.string().max(2).optional().default('es'),
    notes: z.string().nullish(),
    taxable_income_base: z.string().nullish(),
    potential_investment_amount: z.string().nullish(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).optional().default([])
});

/**
 * IndividualResponse
 *
 * Response schema for individual investor data
 */
export const zIndividualResponse = z.object({
    uuid: z.uuid(),
    organization_id: z.int(),
    type: z.literal('individual').default('individual'),
    status: zInvestorStatus,
    first_name: z.string(),
    last_name: z.string(),
    national_id: z.string(),
    birth_date: z.iso.date().nullable(),
    profession: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    communication_language: z.string(),
    notes: z.string().nullable(),
    taxable_income_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    potential_investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).default([]),
    display_name: z.string(),
    full_name: z.string(),
    full_address: z.string().nullable(),
    tax_identifier: z.string(),
    hub_status: zHubStatus.default('none'),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime().nullable()
});

/**
 * IndividualUpdateRequest
 *
 * Request schema for updating an individual investor
 */
export const zIndividualUpdateRequest = z.object({
    status: zInvestorStatus.nullish(),
    first_name: z.string().max(120).nullish(),
    last_name: z.string().max(160).nullish(),
    national_id: z.string().max(32).nullish(),
    birth_date: z.iso.date().nullish(),
    profession: z.string().max(160).nullish(),
    company_name: z.string().nullish(),
    tax_id: z.string().nullish(),
    legal_rep_first_name: z.string().nullish(),
    legal_rep_last_name: z.string().nullish(),
    website: z.string().nullish(),
    email: z.email().nullish(),
    phone: z.string().max(50).nullish(),
    address: z.string().nullish(),
    city: z.string().max(120).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    communication_language: z.string().max(2).nullish(),
    notes: z.string().nullish(),
    taxable_income_base: z.string().nullish(),
    potential_investment_amount: z.string().nullish(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).nullish()
});

/**
 * InvestorProfileResponse
 *
 * Investor profile for Hub - public fields visible to investor.
 * Excludes: internal notes, audit fields, organization_id
 */
export const zInvestorProfileResponse = z.object({
    uuid: z.uuid(),
    type: zInvestorType,
    status: zInvestorStatus,
    display_name: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    national_id: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    profession: z.string().nullable(),
    company_name: z.string().nullable(),
    tax_id: z.string().nullable(),
    legal_rep_first_name: z.string().nullable(),
    legal_rep_last_name: z.string().nullable(),
    legal_rep_national_id: z.string().nullable(),
    website: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    communication_language: z.string().default('es'),
    full_address: z.string().nullable(),
    taxable_income_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    potential_investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).default([])
});

/**
 * InvestorProfileUpdateRequest
 *
 * Update request for Hub profile - full editing capabilities.
 * Excludes: type (immutable), status (admin-controlled), notes (internal).
 */
export const zInvestorProfileUpdateRequest = z.object({
    first_name: z.string().max(120).nullish(),
    last_name: z.string().max(120).nullish(),
    national_id: z.string().max(20).nullish(),
    birth_date: z.iso.date().nullish(),
    profession: z.string().max(120).nullish(),
    company_name: z.string().max(255).nullish(),
    tax_id: z.string().max(20).nullish(),
    legal_rep_first_name: z.string().max(120).nullish(),
    legal_rep_last_name: z.string().max(120).nullish(),
    legal_rep_national_id: z.string().max(20).nullish(),
    website: z.string().max(255).nullish(),
    email: z.email().nullish(),
    phone: z.string().max(50).nullish(),
    address: z.string().nullish(),
    city: z.string().max(120).nullish(),
    postal_code: z.string().max(20).nullish(),
    country: z.string().max(2).nullish(),
    communication_language: z.string().max(2).nullish(),
    taxable_income_base: z.string().nullish(),
    potential_investment_amount: z.string().nullish(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).nullish()
});

/**
 * InvestorWithInvestmentSummaryResponse
 *
 * Response for investor with nested investment summary
 */
export const zInvestorWithInvestmentSummaryResponse = z.object({
    uuid: z.uuid(),
    type: zInvestorType,
    status: zInvestorStatus,
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    company_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    taxable_income_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    potential_investment_amount: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    cultural_interests: z.array(zAppDomainInvestorsEnumsCulturalActivity).default([]),
    investment_summary: zInvestmentSummary,
    display_name: z.string(),
    tax_identifier: z.string().nullable()
});

/**
 * PaginatedResponse[InvestorWithInvestmentSummaryResponse]
 */
export const zPaginatedResponseInvestorWithInvestmentSummaryResponse = z.object({
    items: z.array(zInvestorWithInvestmentSummaryResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * DeductionMetricsResponse
 *
 * Response model for deduction metrics
 */
export const zAppSchemasBeneficiariesDeductionMetricsResponse = z.object({
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    used: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    utilization_percentage: z.number(),
    revised: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/)
});

/**
 * BeneficiaryCurrentYearQuotaSummary
 *
 * Summary model for beneficiary current year fiscal quota with nested metrics
 */
export const zBeneficiaryCurrentYearQuotaSummary = z.object({
    fiscal_year: z.int(),
    deduction: zAppSchemasBeneficiariesDeductionMetricsResponse,
    investment: zInvestmentMetricsResponse
});

/**
 * BeneficiaryResponseWithStats
 *
 * Response model for beneficiary data with statistics
 */
export const zBeneficiaryResponseWithStats = z.object({
    uuid: z.uuid(),
    name: z.string(),
    tax_id: z.string(),
    company_type: zCompanyType,
    main_activities: z.array(zAppDomainBeneficiariesEnumsCulturalActivity),
    status: zEntityStatus,
    constitution_date: z.iso.date().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    notes: z.string().nullable(),
    business_purpose: z.string().nullable(),
    cnae_code: z.string().nullable(),
    last_year_revenue: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable(),
    employee_count: z.int().nullable(),
    incentive_type: zTaxIncentiveType.nullable(),
    signature_regime: zSignatureRegime.default('sole'),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    total_projects_count: z.int(),
    current_year_quota: zBeneficiaryCurrentYearQuotaSummary.nullable(),
    has_available_quota: z.boolean()
});

/**
 * FiscalQuotaResponse
 *
 * Response model for fiscal quota data with nested metrics
 */
export const zFiscalQuotaResponse = z.object({
    uuid: z.uuid(),
    fiscal_year: z.int(),
    comments: z.string().nullable(),
    default_profitability_percentage: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable(),
    deduction_rate: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    deduction: zAppSchemasBeneficiariesDeductionMetricsResponse,
    investment: zInvestmentMetricsResponse,
    linked_projects: z.array(zLinkedProjectSummaryResponse).default([]),
    cost_breakdown: z.record(z.string(), z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)).nullable(),
    total_budget: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_eligible_cost: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    deduction_base: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    total_generated_deduction: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    own_contribution: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/).nullable(),
    subsidies: z.array(z.record(z.string(), z.unknown())).nullable(),
    consolidated_breakdown: z.record(z.string(), z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)).nullable(),
    consolidated_total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullable(),
    consolidated_at: z.iso.datetime().nullable(),
    consolidated_by_id: z.int().nullable(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime()
});

/**
 * BeneficiaryWithDetailsResponse
 *
 * Response model for beneficiary with all nested details
 */
export const zBeneficiaryWithDetailsResponse = z.object({
    beneficiary: zBeneficiaryResponse,
    legal_representatives: z.array(zBeneficiaryLegalRepresentativeResponse),
    bank_accounts: z.array(zBeneficiaryBankAccountResponse),
    fiscal_quotas: z.array(zFiscalQuotaResponse),
    is_complete: z.boolean()
});

/**
 * FiscalYearSummaryResponse
 *
 * Response for GET /beneficiaries/{uuid}/fiscal-year-summary?year=N.
 */
export const zFiscalYearSummaryResponse = z.object({
    fiscal_year: z.int(),
    quota_count: z.int(),
    aggregate: zFiscalYearAggregateResponse,
    quotas: z.array(zFiscalQuotaResponse)
});

/**
 * ListResponse[FiscalQuotaResponse]
 */
export const zListResponseFiscalQuotaResponse = z.object({
    items: z.array(zFiscalQuotaResponse),
    total_count: z.int()
});

/**
 * PaginatedResponse[BeneficiaryResponseWithStats]
 */
export const zPaginatedResponseBeneficiaryResponseWithStats = z.object({
    items: z.array(zBeneficiaryResponseWithStats),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * DeductionMetricsResponse
 *
 * Response model for fiscal deduction metrics.
 */
export const zAppSchemasDashboardDeductionMetricsResponse = z.object({
    total: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    reserved: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    completed: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    available: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/),
    utilization_rate: z.number(),
    revised_from_projects: z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d{0,2}0*$/).nullish()
});

/**
 * BeneficiaryQuotaMetricsResponse
 *
 * Response model for beneficiary quota metrics.
 * Business concept: "Cupo de Inversión" (Supply Side)
 */
export const zBeneficiaryQuotaMetricsResponse = z.object({
    deduction: zAppSchemasDashboardDeductionMetricsResponse,
    investment_capacity: zInvestmentCapacityMetricsResponse
});

/**
 * DashboardMetricsResponse
 *
 * Main response model for dashboard metrics endpoint.
 *
 * Structured around three business perspectives:
 * - beneficiary_quota: Supply side (Cupo de Inversión)
 * - investor_capacity: Demand side (Capacidad de Inversión)
 * - investments: Transactions (Inversión)
 */
export const zDashboardMetricsResponse = z.object({
    beneficiary_quota: zBeneficiaryQuotaMetricsResponse,
    investor_capacity: zInvestorCapacityMetricsResponse,
    investments: zInvestmentsMetricsResponse,
    contracts: zContractsMetricsResponse,
    metadata: zDashboardMetadataResponse
});

export const zAuthLoginUserBody = zAuthLoginRequest;

/**
 * Successful Response
 */
export const zAuthLoginUserResponse = zAuthTokenResponse;

export const zAuthRefreshTokenBody = zAuthRefreshRequest;

/**
 * Successful Response
 */
export const zAuthRefreshTokenResponse = zAuthTokenResponse;

export const zAuthLogoutBody = zAuthLogoutRequest;

/**
 * Successful Response
 */
export const zAuthLogoutResponse = z.void();

/**
 * Successful Response
 */
export const zSendVerifyEmailResponse = z.void();

export const zSendForgotPasswordEmailBody = zAuthResetPasswordRequest;

/**
 * Successful Response
 */
export const zSendForgotPasswordEmailResponse = z.void();

export const zResetPasswordBody = zAuthForgotPasswordRequest;

/**
 * Successful Response
 */
export const zResetPasswordResponse = z.void();

export const zChangePasswordBody = zAuthChangePasswordRequest;

/**
 * Successful Response
 */
export const zChangePasswordResponse = z.void();

export const zSwitchOrganizationBody = zAuthSwitchOrganizationRequest;

/**
 * Successful Response
 */
export const zSwitchOrganizationResponse = zAuthTokenResponse;

export const zConfirmEmailVerificationBody = zAuthVerifyEmailRequest;

/**
 * Successful Response
 */
export const zConfirmEmailVerificationResponse = z.void();

export const zCreateUserBody = zUserCreateRequest;

/**
 * Successful Response
 */
export const zCreateUserResponse = zUserResponse;

/**
 * Successful Response
 */
export const zGetMeResponse = zUserResponse;

export const zUpdateMeBody = zUserUpdate;

/**
 * Successful Response
 */
export const zUpdateMeResponse = zUserResponse;

/**
 * Successful Response
 */
export const zGetMyMembershipsResponse = zListResponseMembershipResponse;

/**
 * Successful Response
 */
export const zGetMyInvitationsResponse = zListResponseUserInvitationResponse;

export const zSetDefaultMembershipBody = zSetDefaultOrganizationRequest;

/**
 * Successful Response
 */
export const zSetDefaultMembershipResponse = z.void();

export const zHandleDocusignWebhookHeaders = z.object({
    'x-docusign-signature-1': z.string().nullish(),
    'x-authorization-digest': z.string().nullish()
});

/**
 * Response Handle Docusign Webhook
 *
 * Successful Response
 */
export const zHandleDocusignWebhookResponse = z.record(z.string(), z.unknown());

export const zHandleDocusealWebhookHeaders = z.object({
    'x-docuseal-signature': z.string().nullish()
});

/**
 * Response Handle Docuseal Webhook
 *
 * Successful Response
 */
export const zHandleDocusealWebhookResponse = z.record(z.string(), z.unknown());

/**
 * Successful Response
 */
export const zDeleteAllNotificationsResponse = z.void();

export const zGetNotificationsQuery = z.object({
    is_read: z.boolean().nullish(),
    notification_type: zNotificationType.nullish(),
    search: z.string().nullish(),
    created_after: z.iso.datetime().nullish(),
    created_before: z.iso.datetime().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetNotificationsResponse = zPaginatedResponseNotificationResponse;

/**
 * Successful Response
 */
export const zGetNotificationStatsResponse = zNotificationStatsResponse;

export const zDeleteNotificationPath = z.object({
    notification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteNotificationResponse = z.void();

export const zGetNotificationPath = z.object({
    notification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetNotificationResponse = zNotificationResponse;

export const zMarkAsReadPath = z.object({
    notification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zMarkAsReadResponse = z.void();

export const zMarkAsUnreadPath = z.object({
    notification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zMarkAsUnreadResponse = z.void();

/**
 * Successful Response
 */
export const zMarkAllAsReadResponse = z.void();

/**
 * Successful Response
 */
export const zGetCurrentOrganizationResponse = zOrganizationResponse;

export const zUpdateOrganizationBody = zOrganizationUpdateRequest;

/**
 * Successful Response
 */
export const zUpdateOrganizationResponse = zOrganizationResponse;

export const zListOrganizationDocumentsQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListOrganizationDocumentsResponse = zPaginatedResponseDocumentWithReviewResponse;

export const zUploadOrganizationDocumentBody = zBodyUploadOrganizationDocument;

/**
 * Successful Response
 */
export const zUploadOrganizationDocumentResponse = zDocumentWithReviewResponse;

export const zDeleteOrganizationDocumentPath = z.object({
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteOrganizationDocumentResponse = z.void();

export const zGetOrganizationDocumentPath = z.object({
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetOrganizationDocumentResponse = zDocumentWithReviewResponse;

export const zUpdateOrganizationDocumentBody = zDocumentUpdateRequest;

export const zUpdateOrganizationDocumentPath = z.object({
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateOrganizationDocumentResponse = zDocumentWithReviewResponse;

export const zDownloadOrganizationDocumentPath = z.object({
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDownloadOrganizationDocumentResponse = zDocumentDownloadResponse;

export const zReviewOrganizationDocumentBody = zReviewUpdateRequest;

export const zReviewOrganizationDocumentPath = z.object({
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewOrganizationDocumentResponse = zDocumentWithReviewResponse;

/**
 * Successful Response
 */
export const zGetInvitationsResponse = zListResponseInvitationResponse;

export const zCreateInvitationBody = zInvitationCreateRequest;

/**
 * Successful Response
 */
export const zCreateInvitationResponse = zInvitationResponse;

export const zVerifyInvitationQuery = z.object({
    token: z.string(),
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zVerifyInvitationResponse = zInvitationResponse;

export const zResendInvitationPath = z.object({
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zResendInvitationResponse = zInvitationResponse;

export const zCompleteInvitationBody = zInvitationCompletionRequest;

export const zCompleteInvitationPath = z.object({
    invitation_uuid: z.uuid()
});

export const zCompleteInvitationQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zCompleteInvitationResponse = zInvitationCompletionResponse;

export const zRevokeInvitationPath = z.object({
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zRevokeInvitationResponse = z.void();

/**
 * Successful Response
 */
export const zGetMembersResponse = zListResponseMembershipResponse;

export const zRemoveMembershipPath = z.object({
    membership_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zRemoveMembershipResponse = z.void();

export const zUpdateMembershipBody = zMembershipUpdateRequest;

export const zUpdateMembershipPath = z.object({
    membership_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateMembershipResponse = zMembershipResponse;

export const zGetBeneficiariesQuery = z.object({
    status: zEntityStatus.nullish(),
    sector: zAppDomainBeneficiariesEnumsCulturalActivity.nullish(),
    search: z.string().nullish(),
    sort_by: z.enum([
        'available',
        'created_at',
        'name',
        'quota_occupied',
        'sector',
        'total_projects_count',
        'volume'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetBeneficiariesResponse = zPaginatedResponseBeneficiaryResponseWithStats;

export const zCreateBeneficiaryBody = zBeneficiaryCreateRequest;

/**
 * Successful Response
 */
export const zCreateBeneficiaryResponse = zBeneficiaryResponse;

export const zGetBeneficiaryMetricsQuery = z.object({
    status: zEntityStatus.nullish(),
    sector: zAppDomainBeneficiariesEnumsCulturalActivity.nullish(),
    search: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetBeneficiaryMetricsResponse = zBeneficiaryMetricsResponse;

export const zPreviewBeneficiariesExportQuery = z.object({
    status: zEntityStatus.nullish(),
    sector: zAppDomainBeneficiariesEnumsCulturalActivity.nullish(),
    search: z.string().nullish()
});

/**
 * Successful Response
 */
export const zPreviewBeneficiariesExportResponse = zExportPreviewResponse;

export const zExportBeneficiariesQuery = z.object({
    status: zEntityStatus.nullish(),
    sector: zAppDomainBeneficiariesEnumsCulturalActivity.nullish(),
    search: z.string().nullish(),
    sort_by: z.enum([
        'available',
        'created_at',
        'name',
        'quota_occupied',
        'sector',
        'total_projects_count',
        'volume'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    format: zExportFormat.optional().default('csv'),
    columns: z.string().nullish()
});

export const zDeleteBeneficiaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteBeneficiaryResponse = z.void();

export const zGetBeneficiaryDetailsPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetBeneficiaryDetailsResponse = zBeneficiaryWithDetailsResponse;

export const zUpdateBeneficiaryBody = zBeneficiaryUpdateRequest;

export const zUpdateBeneficiaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateBeneficiaryResponse = zBeneficiaryResponse;

export const zListLegalRepresentativesPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zListLegalRepresentativesResponse = zListResponseBeneficiaryLegalRepresentativeResponse;

export const zCreateLegalRepresentativeBody = zBeneficiaryLegalRepresentativeCreateRequest;

export const zCreateLegalRepresentativePath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateLegalRepresentativeResponse = zBeneficiaryLegalRepresentativeResponse;

export const zDeleteLegalRepresentativePath = z.object({
    beneficiary_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteLegalRepresentativeResponse = z.void();

export const zUpdateLegalRepresentativeBody = zBeneficiaryLegalRepresentativeUpdateRequest;

export const zUpdateLegalRepresentativePath = z.object({
    beneficiary_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateLegalRepresentativeResponse = zBeneficiaryLegalRepresentativeResponse;

export const zSetPrimaryLegalRepresentativePath = z.object({
    beneficiary_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSetPrimaryLegalRepresentativeResponse = zBeneficiaryLegalRepresentativeResponse;

export const zListBankAccountsPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zListBankAccountsResponse = zListResponseBeneficiaryBankAccountResponse;

export const zCreateBankAccountBody = zBeneficiaryBankAccountCreateRequest;

export const zCreateBankAccountPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateBankAccountResponse = zBeneficiaryBankAccountResponse;

export const zDeleteBankAccountPath = z.object({
    beneficiary_uuid: z.uuid(),
    bank_account_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteBankAccountResponse = z.void();

export const zUpdateBankAccountBody = zBeneficiaryBankAccountUpdateRequest;

export const zUpdateBankAccountPath = z.object({
    beneficiary_uuid: z.uuid(),
    bank_account_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateBankAccountResponse = zBeneficiaryBankAccountResponse;

export const zSetPrimaryBankAccountPath = z.object({
    beneficiary_uuid: z.uuid(),
    bank_account_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSetPrimaryBankAccountResponse = zBeneficiaryBankAccountResponse;

export const zListFiscalQuotasPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zListFiscalQuotasResponse = zListResponseFiscalQuotaResponse;

export const zCreateFiscalQuotaBody = zFiscalQuotaCreateRequest;

export const zCreateFiscalQuotaPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateFiscalQuotaResponse = zFiscalQuotaResponse;

export const zDeleteFiscalQuotaPath = z.object({
    beneficiary_uuid: z.uuid(),
    fiscal_quota_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteFiscalQuotaResponse = z.void();

export const zUpdateFiscalQuotaBody = zFiscalQuotaUpdateRequest;

export const zUpdateFiscalQuotaPath = z.object({
    beneficiary_uuid: z.uuid(),
    fiscal_quota_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateFiscalQuotaResponse = zFiscalQuotaResponse;

export const zGetFiscalYearSummaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetFiscalYearSummaryQuery = z.object({
    year: z.int()
});

/**
 * Successful Response
 */
export const zGetFiscalYearSummaryResponse = zFiscalYearSummaryResponse;

export const zPreviewFiscalQuotaConsolidationPath = z.object({
    beneficiary_uuid: z.uuid(),
    fiscal_quota_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zPreviewFiscalQuotaConsolidationResponse = zConsolidationPreviewResponse;

export const zCommitFiscalQuotaConsolidationBody = zConsolidationCommitRequest;

export const zCommitFiscalQuotaConsolidationPath = z.object({
    beneficiary_uuid: z.uuid(),
    fiscal_quota_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCommitFiscalQuotaConsolidationResponse = zFiscalQuotaResponse;

export const zListBeneficiaryDocumentsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zListBeneficiaryDocumentsQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(200),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListBeneficiaryDocumentsResponse = zBeneficiaryPortfolioResponse;

export const zUploadBeneficiaryDocumentBody = zBodyUploadBeneficiaryDocument;

export const zUploadBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUploadBeneficiaryDocumentResponse = zDocumentWithReviewResponse;

export const zListBeneficiaryDocumentsByFiscalQuotaPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zListBeneficiaryDocumentsByFiscalQuotaQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    review_status: zReviewStatus.nullish(),
    search: z.string().nullish(),
    per_group_limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zListBeneficiaryDocumentsByFiscalQuotaResponse = zBeneficiaryDocumentsByFiscalQuotaResponse;

export const zDeleteBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteBeneficiaryDocumentResponse = z.void();

export const zGetBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetBeneficiaryDocumentResponse = zDocumentWithReviewResponse;

export const zUpdateBeneficiaryDocumentBody = zDocumentUpdateRequest;

export const zUpdateBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateBeneficiaryDocumentResponse = zDocumentWithReviewResponse;

export const zDownloadBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDownloadBeneficiaryDocumentResponse = zDocumentDownloadResponse;

export const zReviewBeneficiaryDocumentBody = zReviewUpdateRequest;

export const zReviewBeneficiaryDocumentPath = z.object({
    beneficiary_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewBeneficiaryDocumentResponse = zDocumentWithReviewResponse;

export const zGetProjectsQuery = z.object({
    status: zEntityStatus.nullish(),
    subtype: zProjectSubtype.nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    fiscal_year: z.int().nullish(),
    sort_by: z.enum([
        'created_at',
        'fiscal_year',
        'fundraising_deadline',
        'incentive',
        'status',
        'title'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zGetProjectsResponse = zFilteredPaginatedResponseProjectResponse;

export const zDeleteProjectPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteProjectResponse = z.void();

export const zGetProjectPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetProjectResponse = zProjectDetailResponse;

export const zUpdateProjectBody = zProjectUpdateRequest;

export const zUpdateProjectPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateProjectResponse = zProjectResponse;

export const zGetBeneficiaryProjectsPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetBeneficiaryProjectsResponse = zListResponseProjectResponse;

export const zCreateProjectForBeneficiaryBody = zProjectCreateRequest;

export const zCreateProjectForBeneficiaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateProjectForBeneficiaryResponse = zProjectResponse;

export const zCreateProjectsBulkForBeneficiaryBody = zProjectBulkCreateRequest;

export const zCreateProjectsBulkForBeneficiaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateProjectsBulkForBeneficiaryResponse = zProjectBulkCreateResponse;

export const zDeleteProjectPosterPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteProjectPosterResponse = z.void();

export const zGetProjectPosterPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetProjectPosterResponse = zDocumentDownloadResponse;

export const zCreateProjectPosterBody = zBodyCreateProjectPoster;

export const zCreateProjectPosterPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateProjectPosterResponse = zDocumentWithReviewResponse;

export const zListProjectDocumentsPath = z.object({
    project_uuid: z.uuid()
});

export const zListProjectDocumentsQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListProjectDocumentsResponse = zPaginatedResponseDocumentWithReviewResponse;

export const zUploadProjectDocumentBody = zBodyUploadProjectDocument;

export const zUploadProjectDocumentPath = z.object({
    project_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUploadProjectDocumentResponse = zDocumentWithReviewResponse;

export const zDeleteProjectDocumentPath = z.object({
    project_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteProjectDocumentResponse = z.void();

export const zGetProjectDocumentPath = z.object({
    project_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetProjectDocumentResponse = zDocumentWithReviewResponse;

export const zUpdateProjectDocumentBody = zDocumentUpdateRequest;

export const zUpdateProjectDocumentPath = z.object({
    project_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateProjectDocumentResponse = zDocumentWithReviewResponse;

export const zDownloadProjectDocumentPath = z.object({
    project_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDownloadProjectDocumentResponse = zDocumentDownloadResponse;

export const zReviewProjectDocumentBody = zReviewUpdateRequest;

export const zReviewProjectDocumentPath = z.object({
    project_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewProjectDocumentResponse = zDocumentWithReviewResponse;

export const zGetInvestorMetricsQuery = z.object({
    status: zInvestorStatus.nullish(),
    investor_type: zInvestorType.nullish(),
    search: z.string().nullish(),
    fiscal_year: z.int().gte(2000).lte(2100).nullish()
});

/**
 * Successful Response
 */
export const zGetInvestorMetricsResponse = zInvestorMetricsResponse;

export const zListInvestorsQuery = z.object({
    status: zInvestorStatus.nullish(),
    investor_type: zInvestorType.nullish(),
    search: z.string().nullish(),
    uuid: z.uuid().nullish(),
    sort_by: z.enum([
        'name',
        'number_investments',
        'potential_investment',
        'total_investment',
        'type'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(10),
    offset: z.int().gte(0).optional().default(0),
    fiscal_year: z.int().gte(2000).lte(2100).nullish()
});

/**
 * Successful Response
 */
export const zListInvestorsResponse = zPaginatedResponseInvestorWithInvestmentSummaryResponse;

export const zPreviewInvestorsExportQuery = z.object({
    status: zInvestorStatus.nullish(),
    investor_type: zInvestorType.nullish(),
    search: z.string().nullish()
});

/**
 * Successful Response
 */
export const zPreviewInvestorsExportResponse = zExportPreviewResponse;

export const zExportInvestorsQuery = z.object({
    status: zInvestorStatus.nullish(),
    investor_type: zInvestorType.nullish(),
    search: z.string().nullish(),
    sort_by: z.enum([
        'name',
        'number_investments',
        'potential_investment',
        'total_investment',
        'type'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    format: zExportFormat.optional().default('csv'),
    columns: z.string().nullish()
});

export const zCreateIndividualInvestorBody = zIndividualCreateRequest;

/**
 * Successful Response
 */
export const zCreateIndividualInvestorResponse = zIndividualResponse;

export const zGetInvestorMusicDeductionSummaryPath = z.object({
    investor_uuid: z.uuid()
});

export const zGetInvestorMusicDeductionSummaryQuery = z.object({
    fiscal_year: z.int().gte(2000).lte(2100)
});

/**
 * Successful Response
 */
export const zGetInvestorMusicDeductionSummaryResponse = zInvestorMusicDeductionSummaryResponse;

export const zGetIndividualInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetIndividualInvestorResponse = zIndividualResponse;

export const zUpdateIndividualInvestorBody = zIndividualUpdateRequest;

export const zUpdateIndividualInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateIndividualInvestorResponse = zIndividualResponse;

export const zCreateCompanyInvestorBody = zCompanyCreateRequest;

/**
 * Successful Response
 */
export const zCreateCompanyInvestorResponse = zCompanyResponse;

export const zGetCompanyInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetCompanyInvestorResponse = zCompanyResponse;

export const zUpdateCompanyInvestorBody = zCompanyUpdateRequest;

export const zUpdateCompanyInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateCompanyInvestorResponse = zCompanyResponse;

export const zDeleteInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteInvestorResponse = z.void();

export const zActivateInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zActivateInvestorResponse = zInvestorOperationResponse;

export const zDeactivateInvestorPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeactivateInvestorResponse = zInvestorOperationResponse;

export const zListInvestorLegalRepresentativesPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zListInvestorLegalRepresentativesResponse = zListResponseInvestorLegalRepresentativeResponse;

export const zCreateInvestorLegalRepresentativeBody = zInvestorLegalRepresentativeCreateRequest;

export const zCreateInvestorLegalRepresentativePath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateInvestorLegalRepresentativeResponse = zInvestorLegalRepresentativeResponse;

export const zDeleteInvestorLegalRepresentativePath = z.object({
    investor_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteInvestorLegalRepresentativeResponse = z.void();

export const zUpdateInvestorLegalRepresentativeBody = zInvestorLegalRepresentativeUpdateRequest;

export const zUpdateInvestorLegalRepresentativePath = z.object({
    investor_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateInvestorLegalRepresentativeResponse = zInvestorLegalRepresentativeResponse;

export const zSetPrimaryInvestorLegalRepresentativePath = z.object({
    investor_uuid: z.uuid(),
    representative_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSetPrimaryInvestorLegalRepresentativeResponse = zInvestorLegalRepresentativeResponse;

export const zInviteInvestorToHubPath = z.object({
    investor_uuid: z.uuid()
});

export const zInviteInvestorToHubQuery = z.object({
    lang: z.string().optional().default('es')
});

/**
 * Successful Response
 */
export const zInviteInvestorToHubResponse = zInvitationResponse;

export const zListInvestorDocumentsPath = z.object({
    investor_uuid: z.uuid()
});

export const zListInvestorDocumentsQuery = z.object({
    doc_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(200),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListInvestorDocumentsResponse = zInvestorPortfolioResponse;

export const zUploadInvestorDocumentBody = zBodyUploadInvestorDocument;

export const zUploadInvestorDocumentPath = z.object({
    investor_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUploadInvestorDocumentResponse = zDocumentWithReviewResponse;

export const zDeleteInvestorDocumentPath = z.object({
    investor_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteInvestorDocumentResponse = z.void();

export const zGetInvestorDocumentPath = z.object({
    investor_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetInvestorDocumentResponse = zDocumentWithReviewResponse;

export const zUpdateInvestorDocumentBody = zDocumentUpdateRequest;

export const zUpdateInvestorDocumentPath = z.object({
    investor_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateInvestorDocumentResponse = zDocumentWithReviewResponse;

export const zDownloadInvestorDocumentPath = z.object({
    investor_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDownloadInvestorDocumentResponse = zDocumentDownloadResponse;

export const zReviewInvestorDocumentBody = zReviewUpdateRequest;

export const zReviewInvestorDocumentPath = z.object({
    investor_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewInvestorDocumentResponse = zDocumentWithReviewResponse;

export const zListProposalsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListProposalsResponse = zPaginatedResponseInvestmentProposalResponse;

export const zCreateProposalBody = zInvestmentProposalCreateRequest;

/**
 * Successful Response
 */
export const zCreateProposalResponse = zInvestmentProposalResponse;

export const zGetProposalPath = z.object({
    proposal_uuid: z.uuid()
});

export const zGetProposalQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zGetProposalResponse = zInvestmentProposalResponse;

export const zAcceptProposalPath = z.object({
    proposal_uuid: z.uuid()
});

export const zAcceptProposalQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zAcceptProposalResponse = z.void();

export const zDeclineProposalPath = z.object({
    proposal_uuid: z.uuid()
});

export const zDeclineProposalQuery = z.object({
    token: z.string()
});

/**
 * Successful Response
 */
export const zDeclineProposalResponse = z.void();

export const zExtendReservationBody = zExtendReservationRequest;

export const zExtendReservationPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zExtendReservationResponse2 = zExtendReservationResponse;

export const zSendReservationContractBody = zSendReservationContractRequest;

export const zSendReservationContractPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSendReservationContractResponse2 = zSendReservationContractResponse;

export const zPreviewReservationContractBody = zPreviewReservationContractRequest;

export const zPreviewReservationContractPath = z.object({
    investment_uuid: z.uuid()
});

export const zResendDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zResendDocumentResponse2 = zResendDocumentResponse;

export const zSendFinancingContractBody = zSendFinancingContractRequest;

export const zSendFinancingContractPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSendFinancingContractResponse2 = zSendFinancingContractResponse;

export const zPreviewFinancingContractBody = zPreviewFinancingContractRequest;

export const zPreviewFinancingContractPath = z.object({
    investment_uuid: z.uuid()
});

export const zSendCommunicationContractBody = zSendCommunicationContractRequest;

export const zSendCommunicationContractPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSendCommunicationContractResponse2 = zSendCommunicationContractResponse;

export const zRetryContractDispatchPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zRetryContractDispatchResponse2 = zRetryContractDispatchResponse;

export const zPreviewCommunicationContractBody = zPreviewCommunicationContractRequest;

export const zPreviewCommunicationContractPath = z.object({
    investment_uuid: z.uuid()
});

export const zVoidContractBody = zVoidContractRequest;

export const zVoidContractPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zVoidContractResponse2 = zVoidContractResponse;

export const zDownloadAeatDocumentsBody = zDownloadAeatDocumentsRequest;

export const zDownloadAeatDocumentsPath = z.object({
    investment_uuid: z.uuid()
});

export const zListInvestmentsQuery = z.object({
    state: z.array(zInvestmentState).nullish(),
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    amount_min: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    amount_max: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    is_archived: z.boolean().nullish(),
    investor_type: zInvestorType.nullish(),
    fiscal_year: z.int().nullish(),
    sort_by: z.enum([
        'amount',
        'beneficiary_name',
        'created_at',
        'investor_name',
        'return_percentage',
        'state'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish().default('desc'),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListInvestmentsResponse = zFilteredPaginatedResponseInvestmentWithDetailsResponse;

export const zCreateInvestmentBody = zInvestmentCreateRequest;

/**
 * Successful Response
 */
export const zCreateInvestmentResponse = zInvestmentResponse;

export const zPreviewInvestmentsExportQuery = z.object({
    state: zInvestmentState.nullish(),
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    amount_min: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    amount_max: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    is_archived: z.boolean().nullish(),
    investor_type: zInvestorType.nullish(),
    fiscal_year: z.int().nullish()
});

/**
 * Successful Response
 */
export const zPreviewInvestmentsExportResponse = zExportPreviewResponse;

export const zExportInvestmentsQuery = z.object({
    state: zInvestmentState.nullish(),
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    amount_min: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    amount_max: z.union([
        z.number(),
        z.string().regex(/^(?!^[-+.]*$)[+-]?0*\d*\.?\d*$/)
    ]).nullish(),
    is_archived: z.boolean().nullish(),
    investor_type: zInvestorType.nullish(),
    fiscal_year: z.int().nullish(),
    sort_by: z.enum([
        'amount',
        'beneficiary_name',
        'created_at',
        'investor_name',
        'return_percentage',
        'state'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    format: zExportFormat.optional().default('csv'),
    columns: z.string().nullish()
});

export const zDeleteInvestmentPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteInvestmentResponse = z.void();

export const zGetInvestmentPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetInvestmentResponse = zInvestmentWithDetailsResponse;

export const zUpdateInvestmentBody = zInvestmentUpdateRequest;

export const zUpdateInvestmentPath = z.object({
    investment_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateInvestmentResponse = zInvestmentResponse;

export const zListInvestmentDocumentsPath = z.object({
    investment_uuid: z.uuid()
});

export const zListInvestmentDocumentsQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListInvestmentDocumentsResponse = zPaginatedResponseInvestmentDocumentResponse;

export const zUploadInvestmentDocumentBody = zBodyUploadInvestmentDocument;

export const zUploadInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid()
});

export const zUploadInvestmentDocumentQuery = z.object({
    document_type: zAppDomainDocumentsEnumsDocumentType,
    title: z.string().nullish()
});

/**
 * Successful Response
 */
export const zUploadInvestmentDocumentResponse = zInvestmentDocumentResponse;

export const zDeleteInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteInvestmentDocumentResponse = z.void();

export const zGetInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetInvestmentDocumentResponse = zInvestmentDocumentResponse;

export const zUpdateInvestmentDocumentBody = zDocumentUpdateRequest;

export const zUpdateInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateInvestmentDocumentResponse = zInvestmentDocumentResponse;

export const zDownloadInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDownloadInvestmentDocumentResponse = zDocumentDownloadResponse;

export const zReviewInvestmentDocumentBody = zReviewUpdateRequest;

export const zReviewInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewInvestmentDocumentResponse = zInvestmentDocumentResponse;

export const zGenerateExpenseAuditBody = zGenerateExpenseAuditRequest;

export const zGenerateExpenseAuditPath = z.object({
    fiscal_quota_uuid: z.uuid()
});

export const zPreviewExpenseAuditBody = zPreviewExpenseAuditRequest;

export const zPreviewExpenseAuditPath = z.object({
    fiscal_quota_uuid: z.uuid()
});

export const zListContractsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0),
    contract_type: z.enum([
        'reservation',
        'financing',
        'communication'
    ]).nullish(),
    status: zContractDisplayStatus.nullish(),
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    document_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    sort_by: z.enum([
        'beneficiary_name',
        'contract_type',
        'created_at',
        'investor_name',
        'sent_at',
        'status',
        'uploaded_at'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish().default('desc')
});

/**
 * Successful Response
 */
export const zListContractsResponse = zPaginatedResponseContractSummary;

export const zGetContractStatsQuery = z.object({
    contract_type: z.enum([
        'reservation',
        'financing',
        'communication'
    ]).nullish(),
    status: zContractDisplayStatus.nullish(),
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetContractStatsResponse = zContractStatsResponse;

export const zGetContractDetailsPath = z.object({
    contract_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetContractDetailsResponse = zContractDetailsResponse;

export const zGetContractDocumentPath = z.object({
    contract_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetContractDocumentResponse = zDocumentDownloadResponse;

export const zListTemplatesQuery = z.object({
    type: zTemplateType.nullish(),
    category: zTemplateCategory.nullish(),
    language: zTemplateLanguage.nullish(),
    industry: zTemplateIndustry.nullish(),
    search: z.string().nullish(),
    sort_by: z.enum([
        'category',
        'name',
        'type',
        'updated_at'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListTemplatesResponse = zPaginatedResponseTemplateSummaryResponse;

export const zCreateTemplateBody = zTemplateCreateRequest;

/**
 * Successful Response
 */
export const zCreateTemplateResponse = zTemplateDetailResponse;

export const zDeleteTemplatePath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteTemplateResponse = z.void();

export const zGetTemplatePath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetTemplateResponse = zTemplateDetailResponse;

export const zUpdateTemplateBody = zTemplateUpdateRequest;

export const zUpdateTemplatePath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateTemplateResponse = zTemplateDetailResponse;

export const zDuplicateTemplateBody = zDuplicateTemplateRequest;

export const zDuplicateTemplatePath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDuplicateTemplateResponse = zTemplateDetailResponse;

export const zUploadTemplateImageBody = zTemplateImageUploadRequest;

export const zUploadTemplateImagePath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUploadTemplateImageResponse = zTemplateImageUploadResponse;

export const zGetTemplateImagePreviewBody = zTemplateImagePreviewRequest;

export const zGetTemplateImagePreviewPath = z.object({
    template_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetTemplateImagePreviewResponse = zTemplateImagePreviewResponse;

export const zPreviewTemplatePath = z.object({
    template_uuid: z.uuid()
});

export const zGetTemplateCompatibilityPath = z.object({
    template_uuid: z.uuid()
});

export const zGetTemplateCompatibilityQuery = z.object({
    action: zCompatibilityAction.nullish(),
    investment_uuid: z.uuid().nullish(),
    include_annexes: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zGetTemplateCompatibilityResponse = zTemplateCompatibilityResponse;

export const zGetCatalogQuery = z.object({
    groups: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetCatalogResponse = zCatalogResponse;

export const zGetDashboardMetricsQuery = z.object({
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_year: z.int().gte(2020).lte(2100).nullish()
});

/**
 * Successful Response
 */
export const zGetDashboardMetricsResponse = zDashboardMetricsResponse;

export const zGetInvestmentMetricsQuery = z.object({
    investor_uuid: z.uuid().nullish(),
    beneficiary_uuid: z.uuid().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_year: z.int().gte(2020).lte(2100).nullish(),
    states: z.array(zInvestmentState).nullish(),
    group_by: z.string().regex(/^(state|beneficiary|investor)$/).optional().default('state'),
    investor_type: zInvestorType.nullish()
});

/**
 * Successful Response
 */
export const zGetInvestmentMetricsResponse = zInvestmentAggregationResponse;

export const zQueryAgentBody = zAgentQueryRequest;

/**
 * Successful Response
 */
export const zQueryAgentResponse = zAgentQueryResponse;

export const zSubmitFeedbackBody = zAgentFeedbackRequest;

/**
 * Successful Response
 */
export const zSubmitFeedbackResponse = z.void();

/**
 * Successful Response
 */
export const zGetOrgEntitlementsResponse = zListResponseModuleEntitlementResponse;

export const zCreateUploadSessionBody = zCreateUploadSessionRequest;

export const zCreateUploadSessionPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zCreateUploadSessionResponse2 = zCreateUploadSessionResponse;

export const zSignUploadBatchBody = zSignUploadBatchRequest;

export const zSignUploadBatchPath = z.object({
    beneficiary_uuid: z.uuid(),
    session_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSignUploadBatchResponse2 = zSignUploadBatchResponse;

export const zGetUploadSessionStatusPath = z.object({
    beneficiary_uuid: z.uuid(),
    session_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetUploadSessionStatusResponse = zUploadSessionStatusResponse;

export const zListClassificationsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zListClassificationsQuery = z.object({
    status: zClassificationStatus.nullish(),
    document_type: z.array(zAppDomainClassificationsEnumsDocumentType).nullish(),
    search: z.string().nullish(),
    eligible: z.boolean().nullish(),
    expense_type: z.string().nullish(),
    eligibility: zEligibility.nullish(),
    requires_review: z.boolean().nullish(),
    rule_id: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_category: z.array(z.string()).nullish(),
    parent_document_uuid: z.uuid().nullish(),
    sort_by: z.enum([
        'created_at',
        'document_date',
        'document_type',
        'filename',
        'status',
        'total_amount'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(50),
    offset: z.int().gte(0).optional().default(0)
});

/**
 * Successful Response
 */
export const zListClassificationsResponse = zFilteredPaginatedResponseClassificationResponse;

export const zPreviewClassificationsExportPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zPreviewClassificationsExportQuery = z.object({
    status: zClassificationStatus.nullish(),
    document_type: z.array(zAppDomainClassificationsEnumsDocumentType).nullish(),
    search: z.string().nullish(),
    eligibility: zEligibility.nullish(),
    requires_review: z.boolean().nullish(),
    rule_id: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_category: z.array(z.string()).nullish(),
    expense_type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zPreviewClassificationsExportResponse = zExportPreviewResponse;

export const zExportClassificationsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zExportClassificationsQuery = z.object({
    status: zClassificationStatus.nullish(),
    document_type: z.array(zAppDomainClassificationsEnumsDocumentType).nullish(),
    search: z.string().nullish(),
    eligibility: zEligibility.nullish(),
    requires_review: z.boolean().nullish(),
    rule_id: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_category: z.array(z.string()).nullish(),
    expense_type: z.string().nullish(),
    sort_by: z.enum([
        'created_at',
        'document_date',
        'document_type',
        'filename',
        'status',
        'total_amount'
    ]).nullish(),
    sort_direction: z.enum(['asc', 'desc']).nullish(),
    format: zExportFormat.optional().default('csv'),
    columns: z.string().nullish()
});

export const zGetClassificationStatsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetClassificationStatsQuery = z.object({
    status: zClassificationStatus.nullish(),
    document_type: z.array(zAppDomainClassificationsEnumsDocumentType).nullish(),
    search: z.string().nullish(),
    eligible: z.boolean().nullish(),
    expense_type: z.string().nullish(),
    eligibility: zEligibility.nullish(),
    requires_review: z.boolean().nullish(),
    rule_id: z.string().nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    fiscal_category: z.array(z.string()).nullish()
});

/**
 * Response Get Classification Stats
 *
 * Successful Response
 */
export const zGetClassificationStatsResponse = z.record(z.string(), z.int());

export const zListRuleIdsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zListRuleIdsQuery = z.object({
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Successful Response
 */
export const zListRuleIdsResponse = zRuleIdsResponse;

export const zGetClassificationSummaryPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetClassificationSummaryQuery = z.object({
    fiscal_quota_uuid: z.uuid().nullish(),
    status: z.string().nullish(),
    document_type: z.string().nullish(),
    eligible: z.boolean().nullish(),
    expense_type: z.string().nullish(),
    eligibility: z.string().nullish(),
    fiscal_category: z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zGetClassificationSummaryResponse = zClassificationSummaryResponse;

export const zGetClassificationPipelineStatusPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetClassificationPipelineStatusResponse = zPipelineStatusBreakdownResponse;

export const zGetSuppliersAnalyticsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetSuppliersAnalyticsQuery = z.object({
    fiscal_quota_uuid: z.uuid().nullish(),
    expense_type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetSuppliersAnalyticsResponse = zSuppliersAnalyticsResponse;

export const zGetTaxBreakdownPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetTaxBreakdownQuery = z.object({
    fiscal_quota_uuid: z.uuid().nullish(),
    expense_type: z.string().nullish()
});

/**
 * Successful Response
 */
export const zGetTaxBreakdownResponse = zTaxBreakdownAnalyticsResponse;

export const zGetWorkforceAnalyticsPath = z.object({
    beneficiary_uuid: z.uuid()
});

export const zGetWorkforceAnalyticsQuery = z.object({
    fiscal_quota_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetWorkforceAnalyticsResponse = zWorkforceAnalyticsResponse;

export const zDeleteClassificationPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zDeleteClassificationResponse = z.void();

export const zGetClassificationPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetClassificationResponse = zClassificationResponse;

export const zUpdateClassificationBody = zUpdateClassificationRequest;

export const zUpdateClassificationPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zUpdateClassificationResponse = zClassificationResponse;

export const zGetClassificationFilePath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetClassificationFileResponse = zDocumentDownloadResponse;

export const zReviewClassificationBody = zReviewClassificationRequest;

export const zReviewClassificationPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReviewClassificationResponse = zClassificationResponse;

export const zResolveDuplicateBody = zResolveDuplicateRequest;

export const zResolveDuplicatePath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zResolveDuplicateResponse2 = zResolveDuplicateResponse;

export const zBulkDeleteClassificationsBody = zBulkDeleteClassificationsRequest;

export const zBulkDeleteClassificationsPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zBulkDeleteClassificationsResponse2 = zBulkDeleteClassificationsResponse;

export const zBulkReclassifyClassificationsBody = zBulkReclassifyClassificationsRequest;

export const zBulkReclassifyClassificationsPath = z.object({
    beneficiary_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zBulkReclassifyClassificationsResponse2 = zBulkReclassifyClassificationsResponse;

export const zGetClassificationLocksPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetClassificationLocksResponse = zClassificationLocksResponse;

/**
 * Payload
 */
export const zReclassifyClassificationBody = zReclassifyClassificationRequest.nullable();

export const zReclassifyClassificationPath = z.object({
    beneficiary_uuid: z.uuid(),
    classification_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zReclassifyClassificationResponse = zClassificationResponse;

/**
 * Successful Response
 */
export const zGetDashboardStatsResponse = zAdminDashboardStats;

export const zListUsersQuery = z.object({
    limit: z.int().gte(1).lte(1000).optional().default(100),
    offset: z.int().gte(0).optional().default(0),
    is_active: z.boolean().nullish(),
    is_platform_admin: z.boolean().nullish()
});

/**
 * Response List Users
 *
 * Successful Response
 */
export const zListUsersResponse = z.array(zUserResponse);

export const zGetUserByUuidPath = z.object({
    user_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetUserByUuidResponse = zUserResponse;

export const zToggleUserStatusPath = z.object({
    user_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zToggleUserStatusResponse = zUserResponse;

export const zGetUserMembershipsPath = z.object({
    user_uuid: z.uuid()
});

/**
 * Response Get User Memberships
 *
 * Successful Response
 */
export const zGetUserMembershipsResponse = z.array(zMembershipResponse);

export const zSendResetPasswordEmailPath = z.object({
    user_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zSendResetPasswordEmailResponse = zMessageResponse;

/**
 * Response List All Organizations
 *
 * Successful Response
 */
export const zListAllOrganizationsResponse = z.array(zOrganizationResponse);

export const zCreateOrganizationBody = zOrganizationCreateRequest;

/**
 * Successful Response
 */
export const zCreateOrganizationResponse = zOrganizationResponse;

export const zAdminDeleteOrganizationPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminDeleteOrganizationResponse = z.void();

export const zGetOrganizationByUuidPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGetOrganizationByUuidResponse = zOrganizationResponse;

export const zAdminUpdateOrganizationBody = zOrganizationUpdateRequest;

export const zAdminUpdateOrganizationPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminUpdateOrganizationResponse = zOrganizationResponse;

export const zAdminInviteUserBody = zInvitationCreateRequest;

export const zAdminInviteUserPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminInviteUserResponse = zInvitationResponse;

export const zListInvitationsPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Response List Invitations
 *
 * Successful Response
 */
export const zListInvitationsResponse = z.array(zInvitationResponse);

export const zAdminDeleteOrganizationInvitationPath = z.object({
    organization_uuid: z.uuid(),
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminDeleteOrganizationInvitationResponse = z.void();

export const zListMembersPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Response List Members
 *
 * Successful Response
 */
export const zListMembersResponse = z.array(zMembershipResponse);

export const zAdminDeleteMembershipPath = z.object({
    membership_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminDeleteMembershipResponse = z.void();

export const zAdminUpdateMembershipBody = zMembershipUpdateRequest;

export const zAdminUpdateMembershipPath = z.object({
    membership_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminUpdateMembershipResponse = zMembershipResponse;

export const zAdminCreateInvitationBody = zAdminInvitationCreateRequest;

/**
 * Successful Response
 */
export const zAdminCreateInvitationResponse = zInvitationResponse;

export const zAdminResendInvitationPath = z.object({
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminResendInvitationResponse = zInvitationResponse;

export const zAdminDeleteInvitationPath = z.object({
    invitation_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminDeleteInvitationResponse = z.void();

export const zListEntitlementsPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Response List Entitlements
 *
 * Successful Response
 */
export const zListEntitlementsResponse = z.array(zAdminEntitlementResponse);

export const zGrantEntitlementBody = zAdminEntitlementCreateRequest;

export const zGrantEntitlementPath = z.object({
    organization_uuid: z.uuid()
});

/**
 * Successful Response
 */
export const zGrantEntitlementResponse = zAdminEntitlementResponse;

export const zUpdateEntitlementBody = zAdminEntitlementUpdateRequest;

export const zUpdateEntitlementPath = z.object({
    organization_uuid: z.uuid(),
    module_code: zModuleCode
});

/**
 * Successful Response
 */
export const zUpdateEntitlementResponse = zAdminEntitlementResponse;

export const zResetEntitlementQuotaPath = z.object({
    organization_uuid: z.uuid(),
    module_code: zModuleCode
});

/**
 * Successful Response
 */
export const zResetEntitlementQuotaResponse = zAdminEntitlementResponse;

export const zGetOrganizationUsagePath = z.object({
    organization_uuid: z.uuid()
});

export const zGetOrganizationUsageQuery = z.object({
    from: z.iso.datetime().nullish(),
    to: z.iso.datetime().nullish()
});

/**
 * Response Get Organization Usage
 *
 * Successful Response
 */
export const zGetOrganizationUsageResponse = z.array(zDomainUsageReportResponse);

/**
 * Successful Response
 */
export const zListMyProfilesResponse = zHubProfilesResponse;

export const zGetMyProfileQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetMyProfileResponse = zInvestorProfileResponse;

export const zUpdateMyProfileBody = zInvestorProfileUpdateRequest;

export const zUpdateMyProfileQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zUpdateMyProfileResponse = zInvestorProfileResponse;

export const zListMyInvestmentsQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(10),
    offset: z.int().gte(0).optional().default(0),
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zListMyInvestmentsResponse = zPaginatedResponseHubInvestmentResponse;

export const zGetMyInvestmentPath = z.object({
    investment_uuid: z.uuid()
});

export const zGetMyInvestmentQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetMyInvestmentResponse = zHubInvestmentDetailResponse;

export const zGetMyDashboardQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetMyDashboardResponse = zHubDashboardSummary;

export const zGetMyPendingActionsQuery = z.object({
    expiring_days: z.int().gte(1).lte(30).optional().default(7),
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetMyPendingActionsResponse = zHubPendingActionsResponse;

export const zGetMyDocumentsPortfolioQuery = z.object({
    doc_type: zAppDomainDocumentsEnumsDocumentType.nullish(),
    status: zDocumentStatus.nullish(),
    fiscal_quota_uuid: z.uuid().nullish(),
    search: z.string().nullish(),
    limit: z.int().gte(1).lte(500).optional().default(200),
    offset: z.int().gte(0).optional().default(0),
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetMyDocumentsPortfolioResponse = zInvestorPortfolioResponse;

export const zListMyInvestmentDocumentsPath = z.object({
    investment_uuid: z.uuid()
});

export const zListMyInvestmentDocumentsQuery = z.object({
    limit: z.int().gte(1).lte(500).optional().default(100),
    offset: z.int().gte(0).optional().default(0),
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zListMyInvestmentDocumentsResponse = zPaginatedResponseInvestmentDocumentResponse;

export const zDownloadMyInvestmentDocumentPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

export const zDownloadMyInvestmentDocumentQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zDownloadMyInvestmentDocumentResponse = zDocumentDownloadResponse;

export const zGetDocumentSigningUrlBody = zHubSigningUrlRequest;

export const zGetDocumentSigningUrlPath = z.object({
    investment_uuid: z.uuid(),
    document_uuid: z.uuid()
});

export const zGetDocumentSigningUrlQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zGetDocumentSigningUrlResponse = zHubSigningUrlResponse;

export const zUploadPaymentReceiptBody = zBodyUploadPaymentReceipt;

export const zUploadPaymentReceiptPath = z.object({
    investment_uuid: z.uuid()
});

export const zUploadPaymentReceiptQuery = z.object({
    investor_uuid: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zUploadPaymentReceiptResponse = zDocumentWithReviewResponse;
